/**
 * Boost AI Search & Discovery — Instant Search panel geometry.
 *
 * `boost-sd-custom.js` / `boost-sd-custom.css` are Boost's own filenames, which is why they
 * carry no `nova-` prefix. Boost loads them itself when its `generalSettings.custom_js_asset_url`
 * / `custom_css_asset_url` are populated; those come back empty on this store, so
 * layout/theme.liquid loads them directly (as a module, for the import below) and this file
 * guards against running twice.
 *
 * Everything visual lives in boost-sd-custom.css. What CSS cannot express is *where the header
 * search input is*: Figma centres the panel on that input, and Boost positions its popup on
 * <body> from the input's rect — writing `top` and `right` inline, in document coordinates,
 * once, and never re-syncing them.
 *
 * So this file publishes the input's live viewport rect as `--live-*` custom properties and
 * hands the actual positioning to CSS, which owns it end to end (`--cp-is-left` / `--cp-is-top`
 * / `--cp-is-width` in the Panel shell section). The split matters because the interesting part
 * — "centre the panel on the input, but never let it overhang either edge of the viewport" — is
 * a `clamp()`, and a `clamp()` in a stylesheet is both shorter and more legible than the same
 * arithmetic recomputed in JS on every scroll frame. It also keeps the responsive knobs
 * (`--cp-is-panel-width`, the narrow-desktop breakpoint) in the file that declares them, where a
 * JS clamp against a hard-coded 1038 could not see them.
 *
 * Why the panel has to be repositioned at all rather than left to Boost:
 *
 *   · Horizontally, Boost right-aligns the popup to the input (`right: <px>; left: auto`) at its
 *     own default width. Widening the wrapper to the design's panel therefore grows it to the
 *     *left*, off the side of the screen on a narrow desktop — which is the bug this replaces.
 *   · Vertically, Boost's inline `top` is in document coordinates and never re-syncs, because it
 *     locks page scroll while the panel is open (its `.boost-sd__search-opening` rule).
 *     boost-sd-custom.css releases that lock on desktop, since the lock's side effect was
 *     removing the page scrollbar and shifting every centred element on the page by its width.
 *     With scrolling live the panel has to follow the sticky header instead of the document, so
 *     the CSS switches it to `position: fixed` and takes `top` from the live rect below.
 *
 * `--live-search-anchor` (1/0) is the gate for that takeover: every rule that repositions the
 * panel sits inside `@container style(--live-search-anchor: 1)`, so if this script never runs —
 * or the header input goes away — Boost's own positioning is left alone rather than being
 * overridden with nothing.
 *
 * The other things CSS cannot do are the mobile panel's bands — see `splitMobilePanel` — and
 * translating the panel's empty states — see `localizePanel`.
 *
 * Separately from the panel's appearance, two rules keep the widget working at all:
 * `primeInstantSearch` gets it to initialize when the shopper reaches the field without moving a
 * mouse, and `rebindSearchInputs` puts Boost back on the input after the header re-renders it out
 * from under it.
 */

import {propsFor, register} from 'prop-for-that';
import {CartAddEvent} from '@theme/events';
import {fetchConfig} from '@theme/utilities';
/*
 * For the length of the CTA's "added" state, so the grid card and the native card can never drift
 * apart on it. Importing the module is also what guarantees the `fly-to-cart` element below is
 * registered by the time a card is pressed: product-form.js is loaded `fetchpriority="low"` from
 * scripts.liquid, so it is not otherwise certain to have run first.
 */
import {ADD_TO_CART_TEXT_ANIMATION_DURATION} from '@theme/product-form';

(() => {
	if (window.__cpBoostCustomizationLoaded) return;
	window.__cpBoostCustomizationLoaded = true;

	/*
	 * Boost rewrites our `id`s to `boost-sd__search-widget-init-input-N` when it binds, and
	 * tags each bound field with this class — so it is the reliable handle for "the inputs
	 * Boost is driving". `.nova-search-input` narrows that to the header fields, excluding
	 * the input inside Boost's own full-screen mobile search bar.
	 */
	const BOUND_CLASS = 'boost-sd__search-widget-init-input';
	const BOUND_INPUT_SELECTOR = `input.nova-search-input.${BOUND_CLASS}`;
	const FALLBACK_INPUT_SELECTOR = 'input.nova-search-input';

	const isVisible = (element) => Boolean(element) && element.getBoundingClientRect().width > 0;

	const getAnchorInput = () =>
		[...document.querySelectorAll(BOUND_INPUT_SELECTOR)].find(isVisible) ??
		[...document.querySelectorAll(FALLBACK_INPUT_SELECTOR)].find(isVisible) ??
		null;

	/**
	 * Re-measure the anchor input. Assigned by the source's `start` below and called by the
	 * shared MutationObserver, so the theme editor swapping the header section out (or Boost
	 * binding a field it has just rendered) re-seeds the geometry.
	 */
	let refreshAnchor = () => {};

	/**
	 * The header search input's live viewport rect, as prop-for-that custom properties.
	 *
	 * A custom source rather than the library's built-in `size`, because what the panel needs is
	 * the input's *position*, which prop-for-that has no source for — and because the input the
	 * panel should track is "whichever bound field is currently visible", not one fixed element,
	 * so a global source that resolves it on each measurement is a better fit than an element
	 * binding that would have to be torn down and re-attached.
	 *
	 * What it buys over the hand-rolled `style.setProperty` this replaces: writes are diffed and
	 * batched into the library's one shared rAF flush, so a scroll that does not move the header
	 * (the sticky case, i.e. most of them) costs no style recalculation at all, and the geometry
	 * lands in the same frame as every other `--live-*` write on the page instead of a second
	 * one of its own.
	 *
	 * Everything is written on every measurement — including `search-anchor: 0` when there is no
	 * input — so there is no per-property teardown to keep in step with the CSS.
	 */
	const searchAnchorSource = {
		key: 'cp-search-anchor',
		scope: 'global',
		start(ctx) {
			const measure = () => {
				const input = getAnchorInput();

				if (!input) {
					ctx.write('search-anchor', 0);
					return;
				}

				const rect = input.getBoundingClientRect();

				ctx.write('search-anchor', 1);
				ctx.write('search-anchor-center', Math.round(rect.left + rect.width / 2));
				ctx.write('search-anchor-bottom', Math.round(rect.bottom));

				/*
				 * `clientWidth`, not `innerWidth`: this is what the panel's centring is clamped
				 * against, and a classic scrollbar is ~10px of `innerWidth` that the panel must
				 * not be tucked under. It is also what a `max-width` media query resolves
				 * against, so the breakpoint and the clamp agree on how wide the viewport is.
				 */
				ctx.write('search-viewport-w', document.documentElement.clientWidth);

				/*
				 * Where the mobile panel starts. The design hangs it off the bottom of the whole
				 * header rather than off the field, which on this header is 25px lower than the
				 * input's own bottom edge — the field's padding and border, then the header's own
				 * bottom padding. Anchoring to the header keeps that gap correct whatever the
				 * header is doing (the announcement bar, the store row, the compact variant)
				 * instead of freezing today's measurement into a `margin-top`, which is what
				 * desktop does for its much larger 2rem drop. Falls back to the input's bottom if
				 * the header markup ever moves.
				 */
				const header = input.closest('header, header-component');
				ctx.write(
					'search-header-bottom',
					Math.round(header ? header.getBoundingClientRect().bottom : rect.bottom)
				);
			};

			/*
			 * prop-for-that coalesces the *writes*; the rect reads are ours, and a scroll event
			 * can fire more than once per frame, so they still get an rAF of their own to keep
			 * this to one forced layout per frame.
			 */
			let frame = 0;
			const schedule = () => {
				if (frame) return;
				frame = requestAnimationFrame(() => {
					frame = 0;
					measure();
				});
			};

			// The header input is inside a section, so the theme editor can swap it out from
			// under us — and Boost re-renders around it on every keystroke.
			const sizeObserver = window.ResizeObserver ? new ResizeObserver(schedule) : null;
			const observed = new WeakSet();
			const observeInputs = () => {
				if (!sizeObserver) return;
				for (const input of document.querySelectorAll(FALLBACK_INPUT_SELECTOR)) {
					if (observed.has(input)) continue;
					observed.add(input);
					sizeObserver.observe(input);
				}
			};

			measure();
			observeInputs();

			window.addEventListener('resize', schedule, {passive: true});
			window.addEventListener('scroll', schedule, {passive: true});

			refreshAnchor = () => {
				observeInputs();
				schedule();
			};

			return () => {
				refreshAnchor = () => {};
				if (frame) cancelAnimationFrame(frame);
				sizeObserver?.disconnect();
				window.removeEventListener('resize', schedule);
				window.removeEventListener('scroll', schedule);
			};
		},
	};

	/**
	 * Wake Boost's Instant Search on intent, rather than on mouse movement.
	 *
	 * Boost defers the whole initialization — binding our inputs, fetching the template, mounting
	 * the panel — behind the first `mousemove` or `touchmove` on `document`. From its
	 * `instant-search.module.js` (`InstantSearchModule.onInitBlock`):
	 *
	 *     qM() ? await init()
	 *          : (document.addEventListener('mousemove', init, {once: true}),
	 *             document.addEventListener('touchmove', init, {once: true}))
	 *
	 * …where `qM()` is `window.innerWidth < 991`. So the mobile panel initializes eagerly and the
	 * desktop one waits for a pointer to move. Focus, click, keydown and typing are not triggers.
	 *
	 * That leaves the desktop field dead in two ordinary cases. A shopper who reloads with the
	 * cursor already parked over it can click and type a whole query into an input Boost has not
	 * bound, and the panel only appears once something unrelated moves the mouse. A shopper who
	 * tabs into it never moves a pointer at all — with no pointer path to the trigger, Instant
	 * Search is simply unreachable from the keyboard, which fails the keyboard operability the
	 * theme holds itself to (WCAG 2.1 AA). That second case is the one that has to be fixed.
	 *
	 * So this dispatches the `mousemove` Boost is waiting for when the shopper reaches for the
	 * field — hovering it or focusing it, whichever comes first. Hover is in there so the
	 * initialization, which fetches the template and the default suggestions, gets the cursor's
	 * travel time to run ahead of the click.
	 *
	 * Deliberately *not* fired on load: the deferral is a real page-weight win, and priming every
	 * visit would put the widget back on the critical path for shoppers who never search.
	 *
	 * Driving Boost's own trigger rather than calling into `boostISWModule` directly, even though
	 * it exposes an `initISWWithContext`: that entry point is half of `onInitBlock`, and reaching
	 * for it would leave the block's click handlers, its bundle integrations and its after-render
	 * hooks unrun, all reconstructed here against internals that are free to move between Boost
	 * releases. The event is the part of that contract meant to be triggered from outside.
	 *
	 * The repeat is for the race, not for the event: those listeners are attached `{once: true}`
	 * only when Boost's block registers, so a `mousemove` dispatched before its bundle has loaded
	 * is lost with nothing to show for it. Re-dispatching until the class Boost stamps on a bound
	 * input appears covers that without having to know when the bundle lands. `bindSearchInputs()`
	 * runs synchronously at the top of Boost's init, before it awaits the template, so that class
	 * marks "init has started" and the loop stops there instead of idling through the fetches.
	 */
	const PRIME_INTERVAL_MS = 250;
	const PRIME_ATTEMPTS = 20;

	let primeTimer = 0;
	let primeAttempts = 0;

	const boostHasBoundAnInput = () => document.querySelector(`.${BOUND_CLASS}`) !== null;

	const primeInstantSearch = () => {
		if (primeTimer || boostHasBoundAnInput()) return;

		const attempt = () => {
			if (boostHasBoundAnInput() || primeAttempts >= PRIME_ATTEMPTS) {
				clearInterval(primeTimer);
				primeTimer = 0;
				return;
			}

			primeAttempts += 1;

			/*
			 * `bubbles: false` holds this to listeners bound on `document` itself — Boost's, and
			 * nothing else: the theme's own pointer tracking is on `pointermove`, and
			 * collection-links binds `mousemove` to its own element rather than the document.
			 */
			document.dispatchEvent(new MouseEvent('mousemove', {bubbles: false}));
		};

		attempt();
		primeTimer = setInterval(attempt, PRIME_INTERVAL_MS);
	};

	/**
	 * Re-bind after the header re-renders.
	 *
	 * Boost takes the field over by mutating it in place — rewriting its `id`, adding
	 * `.boost-sd__search-widget-init-input`, and hanging its `input`/`keydown` listeners off that
	 * one node. None of that survives the header being re-rendered: the section comes back from
	 * the server as the markup in `nova-search.liquid`, so the replacement input has our original
	 * `id`, none of Boost's classes and no listeners, and Instant Search is silently dead on a
	 * field that looks exactly right.
	 *
	 * This is not a rare path. The header re-renders on this store shortly after every load, when
	 * the favourite-store row resolves from "Sélectionner un magasin" to the shopper's store —
	 * measured on the homepage, Boost bound the input at ~1950ms and the re-render dropped it at
	 * ~2073ms, 120ms later. That is the window the original bug report describes: focus the field
	 * and nothing happens, reload and it works, depending on which side of that swap you landed.
	 *
	 * `bindSearchInputs()` is the remedy rather than another `mousemove`, because by this point
	 * the controller *is* initialized — Boost's own `{once: true}` trigger listeners were consumed
	 * long ago, so priming has nothing left to trip. It is the module's own re-bind entry point,
	 * exists for exactly this, and is safe to call repeatedly: it re-applies the attributes and
	 * guards the listener attachment behind the `data-boost-event-bound` flag it sets itself.
	 *
	 * Called from the shared MutationObserver, so the field is live again as soon as the re-render
	 * settles rather than at the shopper's next hover. The guards keep that cheap — Boost re-renders
	 * the panel on every keystroke, and in all of those the inputs are still bound and this returns
	 * on the second check.
	 */
	const rebindSearchInputs = () => {
		const module = window.boostISWModule;

		// Not initialized yet: that is priming's job, and calling in before Boost has its search
		// settings would bind the inputs to a controller that cannot answer them.
		if (!module?.contextInitialized) return;

		// Still bound — the common case on a panel re-render.
		if (boostHasBoundAnInput()) return;

		// Nothing to bind: no header field on the page at all (a template without the search).
		if (!document.querySelector(FALLBACK_INPUT_SELECTOR)) return;

		module.bindSearchInputs?.();
	};

	/*
	 * `pointerover` rather than `pointerenter`, because a delegated listener needs the event to
	 * bubble — and it covers mouse, pen and the tap that precedes focus alike. Delegated from the
	 * document so the header's two instances share one pair of listeners and both survive the
	 * theme editor re-rendering the section, as header-search.js does for the clear button.
	 *
	 * Both paths are offered because which one is needed depends only on whether Boost has been
	 * initialized yet, and the shopper reaching for the field is the moment it has to be true.
	 */
	const onSearchIntent = (event) => {
		if (!event.target?.closest?.('.nova-search-form')) return;

		primeInstantSearch();
		rebindSearchInputs();
	};

	/**
	 * Mobile: rebuild the panel into the design's bands.
	 *
	 * Boost renders every suggestion block — suggestions, collections, products, pages — as a
	 * child of `.boost-sd__instant-search-column--others`, in the order they are configured in
	 * its admin. The mobile design keeps that order but alternates the fill: grey behind the
	 * link blocks, white behind the products. That needs the product list to be a *sibling* of
	 * the band rather than an item inside it, and it needs the blocks Boost placed after the
	 * products — "Blogues et pages" — to carry on below in a second grey band of their own.
	 *
	 * Which is the one part of the shape CSS cannot express: `--others` is a single element, and
	 * a background cannot be painted behind a run of its children.
	 *
	 * So the products are lifted out, and everything that followed them inside the band moves
	 * into a clone of it placed underneath — the leading band, the products, the trailing band.
	 * Cloning the class list rather than naming a class of our own is what keeps the two halves
	 * styled identically whatever Boost puts on the original.
	 *
	 * The "view all" CTA then follows the products in, so that it can be sticky against the
	 * bottom of the panel while they are on screen. Left where Boost puts it — a sibling of the
	 * band, after everything — it would sit below the trailing band, at the very end of the
	 * panel, rather than at the end of the "Produits" block where the design draws it.
	 *
	 * Boost re-renders the panel on every keystroke, so this runs from the same MutationObserver
	 * that watches for new inputs — synchronously, before the browser paints the render that
	 * triggered it. It is idempotent: once moved, none of these nodes is where it was looked for,
	 * so the pass its own mutations trigger finds nothing and the observer settles.
	 */
	const splitMobilePanel = () => {
		const bands = document.querySelectorAll(
			'.boost-sd__instant-search-container-mobile .boost-sd__instant-search-column--others'
		);

		for (const band of bands) {
			const parent = band.parentElement;
			const list = band.querySelector(':scope > .boost-sd__instant-search-product-list');

			if (list) {
				// Snapshot before moving anything: `nextElementSibling` walks a live tree.
				const trailing = [];
				for (let node = list.nextElementSibling; node; node = node.nextElementSibling) {
					trailing.push(node);
				}

				band.after(list);

				if (trailing.length) {
					const after = band.cloneNode(false);
					after.removeAttribute('id');
					after.append(...trailing);
					list.after(after);
				}

				// Products first in admin order: the leading band is left with nothing in it, and
				// would otherwise paint as a bare grey strip above them.
				if (!band.firstElementChild) band.remove();
			}

			// Wherever the CTA is now, it belongs at the end of the products.
			const products = parent?.querySelector(
				':scope > .boost-sd__instant-search-product-list'
			);
			const viewAll = parent?.querySelector(':scope > .boost-sd__view-all');
			if (products && viewAll) products.append(viewAll);
		}
	};

	/**
	 * Translate the panel's empty states.
	 *
	 * Every suggestion block now keeps its heading whether or not it has anything to list, so
	 * the panel's shape stops changing as you type — which means each one needs a line of copy
	 * for the empty case. That copy cannot come from Boost: its templates live in its own admin
	 * and its `translate` filter only resolves keys from Boost's own fixed list, so a string of
	 * ours would fall through to its English default in every locale.
	 *
	 * So the templates emit the element with a `data-cp-i18n` key and an English default, and
	 * this swaps in the localized string that layout/theme.liquid puts in `#cp-boost-search-i18n`
	 * from `locales/*.json`. If the JSON is missing the English default is what stays on screen.
	 *
	 * `%CP_QUERY%` is the shopper's term; `%CP_COUNT%` / `%CP_RATING%` are numbers the template
	 * could not know either ("Voir tout (49)", "Disponible en 2 formats", "Note de 5 sur 5"). Each
	 * is carried on the element as `data-cp-query` / `data-cp-count` / `data-cp-rating`, because
	 * the string is localized before there is a value to put in it.
	 *
	 * Runs from the same MutationObserver as `splitMobilePanel`, since Boost re-renders the whole
	 * panel on every keystroke. Idempotent: the write is skipped once the text already matches,
	 * so the mutations it causes settle on the next pass rather than looping.
	 */
	const QUERY_TOKEN = '%CP_QUERY%';
	const COUNT_TOKEN = '%CP_COUNT%';
	const RATING_TOKEN = '%CP_RATING%';
	let strings;

	const getStrings = () => {
		if (strings) return strings;

		// Not cached when the tag is missing: `settings.search_engine` gates it in the layout,
		// and a theme-editor change there should not need a reload to take effect.
		const source = document.getElementById('cp-boost-search-i18n');
		if (!source) return {};

		try {
			strings = JSON.parse(source.textContent);
		} catch {
			strings = {};
		}
		return strings;
	};

	const localizePanel = () => {
		const dictionary = getStrings();

		for (const node of document.querySelectorAll('[data-cp-i18n]')) {
			const template = dictionary[node.dataset.cpI18n];
			if (!template) continue;

			const text = template
				.replace(QUERY_TOKEN, node.dataset.cpQuery ?? '')
				.replace(COUNT_TOKEN, node.dataset.cpCount ?? '')
				.replace(RATING_TOKEN, node.dataset.cpRating ?? '');
			if (node.textContent !== text) node.textContent = text;
		}
	};

	/**
	 * The two things the collection / search grid needs that are neither CSS nor Liquid.
	 *
	 * Everything else about the PLP is in boost-plp.css, or in the Boost admin templates
	 * mirrored under `boostai/src/**` — this is only what needs to run *after* Boost has
	 * rendered, because it depends on what it rendered.
	 *
	 * Runs from the same MutationObserver as the panel work below. Boost re-renders the whole
	 * grid on every filter, sort and page change, so both passes have to be idempotent: each
	 * one checks for its own marker class and returns early on a node it has already handled.
	 */
	const PLP_SWATCH_LIMIT = 5;
	const PLP_DONE = 'cp-plp-done';

	/**
	 * Figma 2001-29219 puts the active-filter chips across the top of the product column, on
	 * their own row between the toolbar and the grid; Boost renders `#bc-refine-by` as the first
	 * child of the filter sidebar. Placement is DOM order, which is the one part of this CSS
	 * cannot reach — `.boost-sd-left` and `.boost-sd-right` are separate grid tracks, so no
	 * amount of `order` moves a node between them.
	 *
	 * Boost keeps writing into the container by id, and `getElementById` does not care where in
	 * the document the node sits, so moving it does not cost us the re-renders.
	 *
	 * Below 750 the chips have a different home: 2001-8187 draws them inside the filter sheet,
	 * under its title, and 2089-11884 — the same row on the mobile grid — is switched off in
	 * the frame. So on mobile the node goes into the sheet's scrolling region instead, and
	 * comes back to the product column above the breakpoint. Which end of the box it lands in
	 * does not matter: `boost-plp.css` orders it first there, because the sort control is moved
	 * into that same box by `moveSortingIntoDrawer` and neither pass can know which ran last.
	 */
	const relocateRefineBy = () => {
		const chips = document.getElementById('bc-refine-by');
		if (!chips) return;

		if (MOBILE_PLP.matches) {
			const sheet = document.querySelector(
				'#boost-sd_filter-tree-mobile .boost-sd__filter-tree-vertical-content'
			);
			if (!sheet || chips.parentElement === sheet) return;

			sheet.prepend(chips);
			return;
		}

		const toolbar = document.querySelector(
			'.boost-sd-right.boost-product-listing-column .boost-sd__toolbar-container'
		);
		if (!toolbar || toolbar.nextElementSibling === chips) return;

		toolbar.after(chips);
	};

	/**
	 * --- The mobile toolbar (Figma 2089-11890) ---------------------------------------------
	 *
	 * Below 750 the design's toolbar is a single row: one filled "Filtrer et trier" pill and the
	 * product count. Boost's mobile toolbar is two controls — a "Trier par" dropdown and an
	 * "Affiner par" toggle — and 328 of page has no room for both plus the count.
	 *
	 * So the sort control moves inside the filter drawer the toggle already opens, and the
	 * toggle is renamed to say so. Both are done here rather than in the Boost templates because
	 * neither element comes from one: Boost renders this button from its shipped runtime chunk
	 * (the DOM carries an id and an `aria-expanded` the template under
	 * `boostai/src/filter-tree/filter-tree-toggle-button/` does not write), so an edit there
	 * would never reach the page — the same situation as the rating filter's stars.
	 *
	 * Both passes are gated on the media query rather than on a class, and both undo themselves
	 * above it, so a resize across the breakpoint — which is what a desktop browser's device
	 * toolbar does constantly — lands in the right state either way.
	 */
	const MOBILE_PLP = window.matchMedia('(max-width: 749px)');

	/**
	 * The label is swapped through the `data-cp-i18n` bridge rather than written here, so the
	 * string lives with the rest of the PLP copy in `locales/*.json` and gets the locale the
	 * page was rendered in. `localizePanel` runs after this in the same pass, so the rename
	 * lands in the same frame the attribute is added.
	 *
	 * Boost's own `aria-label` ("Open Filter Sidebar", untranslated) is dropped with it: an
	 * aria-label wins over the button's text, so leaving it would have the button read out the
	 * old name — and in English — however the visible label was localized. Without it the
	 * accessible name falls back to that visible text, which is what we want it to say.
	 */
	const nameFilterToggle = () => {
		// Boost's runtime writes `…-button-text`; the template under boostai/src/ writes
		// `…-button-title`. Both are matched so this holds if Boost ever starts compiling it.
		const title = document.querySelector(
			'.boost-sd__filter-tree-toggle-button-text, .boost-sd__filter-tree-toggle-button-title'
		);
		if (!title) return;

		if (!MOBILE_PLP.matches) {
			title.removeAttribute('data-cp-i18n');
			return;
		}

		if (title.dataset.cpI18n !== 'plp_filter_sort') title.dataset.cpI18n = 'plp_filter_sort';

		const button = title.closest('.boost-sd__filter-tree-toggle-button');
		if (button) button.removeAttribute('aria-label');
	};

	/**
	 * The two labels the sheet itself carries (Figma 2001-8187), for the same reason as the
	 * button above: both come from Boost's own fixed string list, and neither says what the
	 * design says.
	 *
	 *   · Its title. Boost writes "Affiner par" from `refineMobile`; the design's is "Filtrer
	 *     et trier" — the same string as the pill that opens it, so both read off the one
	 *     `plp_filter_sort` key and can never drift apart.
	 *
	 *   · Its primary button. Boost writes "Afficher les résultats (2844)"; the design's is
	 *     "Voir 2844 produits". The count stays Boost's — it is read straight back out of the
	 *     text Boost just wrote rather than counted here, because the number this button
	 *     promises has to be the number the grid will show, and only Boost knows that.
	 *
	 *   · Its "Tout effacer". Boost's own French is "Effacer tout"; `plp_clear_all` is the
	 *     string the chip row's clear-all already carries (boostai/src/filter-tree/
	 *     refineby.liquid), and the two buttons do the same thing.
	 *
	 * Stripping the non-digits is what makes the count read idempotent: it returns the same
	 * number from Boost's own wording and from ours, so the pass settles after one rewrite
	 * instead of the observer trading swaps with itself. When a filter changes the count, Boost
	 * rewrites the whole label and the next pass picks the new number out of it.
	 */
	const nameDrawerChrome = () => {
		const title = document.querySelector(
			'#boost-sd_filter-tree-mobile .boost-sd__filter-tree-vertical-toolbar-middle'
		);
		const result = document.querySelector(
			'#boost-sd_filter-tree-mobile .boost-sd__filter-tree-vertical-show-result .boost-sd__button-text'
		);
		const clear = document.getElementById('boost-sd__button-toolbar-mobile-clear');

		if (!MOBILE_PLP.matches) {
			title?.removeAttribute('data-cp-i18n');
			result?.removeAttribute('data-cp-i18n');
			clear?.removeAttribute('data-cp-i18n');
			return;
		}

		if (title && title.dataset.cpI18n !== 'plp_filter_sort') {
			title.dataset.cpI18n = 'plp_filter_sort';
		}

		if (clear && clear.dataset.cpI18n !== 'plp_clear_all') clear.dataset.cpI18n = 'plp_clear_all';

		if (!result) return;

		const count = result.textContent.replace(/\D/g, '');
		// No count yet (Boost renders the button before it has a total) — leave its own wording
		// in place rather than swapping in "Voir  produits".
		if (!count) return;

		if (result.dataset.cpCount !== count) result.dataset.cpCount = count;
		if (result.dataset.cpI18n !== 'plp_show_results') result.dataset.cpI18n = 'plp_show_results';
	};

	/**
	 * What moves is `.boost-sd__sorting` — the control itself — and not the
	 * `.boost-sd__toolbar-item--sorting` around it: Boost renders the whole mobile filter drawer
	 * *inside* that item, so the item is an ancestor of the very node we are moving it into and
	 * every attempt threw `HierarchyRequestError`. The emptied item stays where it is and is
	 * taken out of the row with `display: contents` in boost-plp.css, which keeps the drawer in
	 * the document and in Boost's reach.
	 *
	 * Boost re-renders the toolbar on every filter, sort and page change, which puts a fresh
	 * control back in it each time — so this always moves the toolbar's node and drops whichever
	 * one it had put in the drawer before, rather than moving once and trusting it to stay.
	 * Inserting the new node before removing the old one means the drawer is never momentarily
	 * without a sort control, which is what the `:has()` guard in boost-plp.css keys off to
	 * decide whether the toolbar's own slot may be collapsed.
	 */
	const CP_DRAWER_SORTING = 'cp-plp-drawer-sorting';

	const moveSortingIntoDrawer = () => {
		const drawerContent = document.querySelector(
			'#boost-sd_filter-tree-mobile .boost-sd__filter-tree-vertical-content'
		);
		if (!drawerContent) return;

		const parked = drawerContent.querySelector(`.${CP_DRAWER_SORTING}`);

		if (!MOBILE_PLP.matches) {
			// Above the breakpoint the control belongs back in the slot it came from.
			const slot = document.querySelector('.boost-sd__toolbar-top-mobile');
			if (parked && slot) {
				parked.classList.remove(CP_DRAWER_SORTING);
				slot.prepend(parked);
			}
			return;
		}

		const fresh = [...document.querySelectorAll('.boost-sd__toolbar .boost-sd__sorting')].find(
			(node) => !drawerContent.contains(node)
		);
		if (!fresh) return;

		fresh.classList.add(CP_DRAWER_SORTING);
		drawerContent.prepend(fresh);
		if (parked && parked !== fresh) parked.remove();
	};

	/**
	 * 2089-12375 sets the page in effect against the total — "3 de 23" — where the desktop row
	 * shows the whole run of numbers. boost-plp.css keeps only the current number at this width,
	 * so without this the shopper is told which page they are on and nothing about how many
	 * there are.
	 *
	 * The total is read back out of the run Boost renders rather than counted from the product
	 * total, which the page does not know its own page size for. Boost always renders the last
	 * page — a long collection comes through as "1 2 3 4 5 … 119" — so the largest number in the
	 * row is the number of pages whether or not the run is elided.
	 */
	/**
	 * --- The mobile card's image strip (Figma 2089-11918) ----------------------------------
	 *
	 * The strip itself is a scroll-snap container and needs no script: the template emits the
	 * slides and boost-plp.css does the snapping. This draws the dots under it, keeps the pill
	 * on whichever shot is in view — the one thing CSS cannot read — and makes each dot scroll
	 * to its own shot.
	 *
	 * The dots are controls, not decoration. A finger has the swipe, but a pointer does not:
	 * a scroll-snap container is scrolled by a wheel or a scrollbar, and this one has neither
	 * a horizontal wheel axis on most mice nor a scrollbar (boost-plp.css hides it), so below
	 * 750 on a desktop the dots were the only way to reach the second shot — and pressing one
	 * opened the product page instead, because Boost navigates on any click inside a card.
	 * Hence `stopPropagation` in the handler: the press has to die on the dot.
	 *
	 * Each dot is a real button with a padded hit area (the pill itself is drawn by a
	 * pseudo-element, so the target can be bigger than the 10px the frame shows), which also
	 * puts the strip on the keyboard's tab order — the scroll container took arrow keys, but
	 * only once something had scrolled it into focus.
	 */
	const CP_GALLERY_DONE = 'cp-plp-gallery--bound';
	const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

	const paintDots = (gallery) => {
		const dots = gallery.cpDots;
		if (!dots || !gallery.clientWidth) return;

		const index = Math.round(gallery.scrollLeft / gallery.clientWidth);
		if (index === gallery.cpIndex) return;

		gallery.cpIndex = index;
		for (const [i, dot] of [...dots.children].entries()) {
			dot.classList.toggle('cp-plp-dot--active', i === index);
			if (i === index) dot.setAttribute('aria-current', 'true');
			else dot.removeAttribute('aria-current');
		}
	};

	/*
	 * Move the strip to one shot, with an animation where the browser has one.
	 *
	 * Two things stop that animation from ever starting, and neither can be tested for up front:
	 * Chrome cancels a smooth programmatic scroll on a container whose slides carry
	 * `scroll-snap-stop: always`, and a browser told not to animate scrolling at all — the
	 * `--disable-smooth-scrolling` flag, or the OS setting behind it — drops the request without
	 * saying so. Both leave the strip exactly where it was, so two frames later this checks, and
	 * jumps if nothing has moved: the shot is the point, the slide is the flourish.
	 */
	const scrollGalleryTo = (gallery, index) => {
		const left = index * gallery.clientWidth;

		if (REDUCED_MOTION.matches) {
			gallery.scrollLeft = left;
			return;
		}

		const from = gallery.scrollLeft;
		gallery.scrollTo({left, behavior: 'smooth'});
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				if (gallery.scrollLeft === from) gallery.scrollLeft = left;
			});
		});
	};

	/*
	 * "Afficher l'image 2". Localized through the same dictionary as the rest of the PLP copy,
	 * but written here rather than through the `data-cp-i18n` bridge: that bridge swaps
	 * `textContent`, and this string is an `aria-label` on an element whose text is a drawn
	 * pill. The English default stands in if the tag is missing, as it does everywhere else.
	 */
	const dotLabel = (index) => {
		const template = getStrings().plp_gallery_dot;
		const number = String(index + 1);
		return template ? template.replace(COUNT_TOKEN, number) : `Show image ${number}`;
	};

	const buildCardGalleryDots = () => {
		// Nothing to indicate above the breakpoint: the strip is display:none and the card is
		// back to its single shot and hover partner.
		if (!MOBILE_PLP.matches) return;

		for (const gallery of document.querySelectorAll('[data-cp-gallery]')) {
			if (gallery.classList.contains(CP_GALLERY_DONE)) continue;
			gallery.classList.add(CP_GALLERY_DONE);

			const column = gallery.closest('.boost-sd__product-item-grid-view-layout-image');
			const count = gallery.children.length;
			if (!column || count < 2) continue;

			const dots = document.createElement('div');
			dots.className = 'cp-plp-dots';
			for (let i = 0; i < count; i += 1) {
				const dot = document.createElement('button');
				dot.type = 'button';
				dot.className = i === 0 ? 'cp-plp-dot cp-plp-dot--active' : 'cp-plp-dot';
				dot.setAttribute('aria-label', dotLabel(i));
				if (i === 0) dot.setAttribute('aria-current', 'true');
				dot.addEventListener('click', (event) => {
					// The press dies here: Boost sends any click inside a card to the product page.
					event.preventDefault();
					event.stopPropagation();
					scrollGalleryTo(gallery, i);
				});
				dots.append(dot);
			}
			column.append(dots);

			gallery.cpDots = dots;
			gallery.cpIndex = 0;

			/*
			 * One rAF-coalesced read per frame of scrolling. The listener is added once per
			 * gallery element — Boost re-renders the grid on every filter, sort and page change,
			 * and the nodes it discards take their listeners with them.
			 */
			let queued = false;
			gallery.addEventListener(
				'scroll',
				() => {
					if (queued) return;
					queued = true;
					requestAnimationFrame(() => {
						queued = false;
						paintDots(gallery);
					});
				},
				{passive: true}
			);
		}
	};

	/*
	 * Drag-to-scroll for that same strip, the horizontal twin of the quick-view stack's drag
	 * below — same shape, same reasons, and the two are kept apart only because this one has a
	 * snap to restore and a link to swallow.
	 *
	 * Mouse pointers only. A finger pans the strip natively, with momentum and rubber-banding
	 * this could only approximate; taking its pointer events would trade all of that for less.
	 *
	 * Two things make a horizontal drag different from the vertical one. The strip snaps, and
	 * `scroll-snap-type: x mandatory` re-snaps every write to `scrollLeft` on the spot, so the
	 * slides would jump from shot to shot instead of following the cursor: the snap is switched
	 * off for the length of the drag (the `--dragging` class in boost-plp.css) and switched back
	 * on at the end, where an explicit smooth scroll settles on the nearest shot. And every
	 * slide is a link to the product page, so the click the release produces has to be stopped
	 * dead — `preventDefault` for the link, `stopPropagation` for Boost's card handler, which
	 * navigates on any click inside a card whether or not it landed on the anchor.
	 */
	const CP_DRAG_THRESHOLD = 4;
	const CP_GALLERY_DRAGGING = 'cp-plp-gallery--dragging';

	const cardDrag = {
		gallery: null,
		pointerId: -1,
		startX: 0,
		startScrollLeft: 0,
		moved: false,
		suppressClick: false,
	};

	const endCardGalleryDrag = () => {
		cardDrag.gallery?.classList.remove(CP_GALLERY_DRAGGING);
		cardDrag.gallery = null;
		cardDrag.pointerId = -1;
		cardDrag.moved = false;
	};

	const onCardGalleryDragStart = (event) => {
		if (event.pointerType !== 'mouse' || event.button !== 0) return;

		const gallery = event.target.closest?.('[data-cp-gallery]');
		/* Above the breakpoint the strip is `display: none` and has nothing to scroll. */
		if (!gallery || gallery.scrollWidth <= gallery.clientWidth) return;

		cardDrag.gallery = gallery;
		cardDrag.pointerId = event.pointerId;
		cardDrag.startX = event.clientX;
		cardDrag.startScrollLeft = gallery.scrollLeft;
		cardDrag.moved = false;
		cardDrag.suppressClick = false;
	};

	const onCardGalleryDragMove = (event) => {
		const gallery = cardDrag.gallery;
		if (!gallery || event.pointerId !== cardDrag.pointerId) return;

		const delta = event.clientX - cardDrag.startX;

		/*
		 * A few pixels of slop before this counts as a drag, so that the shake in an ordinary
		 * click still opens the product page and never grabs the pointer.
		 */
		if (!cardDrag.moved) {
			if (Math.abs(delta) < CP_DRAG_THRESHOLD) return;
			cardDrag.moved = true;
			gallery.classList.add(CP_GALLERY_DRAGGING);
			/* Keeps the drag alive once the pointer leaves the card, as a scrollbar would. */
			gallery.setPointerCapture?.(event.pointerId);
		}

		gallery.scrollLeft = cardDrag.startScrollLeft - delta;
	};

	const onCardGalleryDragEnd = (event) => {
		const gallery = cardDrag.gallery;
		if (!gallery || event.pointerId !== cardDrag.pointerId) return;

		const dragged = cardDrag.moved;
		const index = gallery.clientWidth
			? Math.round(gallery.scrollLeft / gallery.clientWidth)
			: 0;

		cardDrag.suppressClick = dragged;
		endCardGalleryDrag();

		/* After the class is off, so the snap is back on to hold what this lands on. */
		if (dragged) scrollGalleryTo(gallery, index);
	};

	/*
	 * The swallow, in the capture phase so neither the anchor nor Boost sees the click. A flag
	 * rather than a one-shot listener: if a release produces no click at all — the pointer left
	 * the window, the grid re-rendered under it — the flag is cleared by the next press instead
	 * of sitting armed and eating a click the shopper meant.
	 */
	const onCardGalleryDragClick = (event) => {
		if (!cardDrag.suppressClick) return;

		cardDrag.suppressClick = false;
		event.preventDefault();
		event.stopPropagation();
	};

	/*
	 * Card click: the picture and the title are links, the rest of the card is not.
	 *
	 * Boost's grid runtime sends *any* click inside `.boost-sd__product-item` to the product
	 * page — not only the clicks that land on one of the two anchors its templates render.
	 * Nothing in the markup says so and there is no wrapping `<a>` to delete: the chain from a
	 * card child up to `.boost-sd-container` is all `div`s, and the behaviour is bound by Boost's
	 * own bundle. So it is taken away here instead, by stopping the click before Boost's listener
	 * can see it.
	 *
	 * What keeps navigating is the picture and the title — the two things a shopper reads as
	 * "the product". Everything under the picture (vendor, price, rating, the formats line, the
	 * stock rows) goes back to being ordinary text: selectable, and free to carry controls of its
	 * own without each one having to fight the card for its own click.
	 *
	 * Four cases, in order:
	 *
	 *   · A promotion card (`.cp-plp-promo-item`, see `injectPromotions`) is always stopped. It
	 *     wears Boost's item classes so the grid lays it out, but it has no product behind it, so
	 *     Boost's handler sends it to the store root instead of the metaobject's link. Its overlay
	 *     `<a>` still navigates natively; the touch tap-hold in promotion-card.js listens on
	 *     `window` in capture phase, so it runs before this stops the click.
	 *   · A real control anywhere in the card is never touched — both of Boost's anchors, the
	 *     swatch radios and their labels, the "more options" button, the CTA buttons, the
	 *     wishlist button, the gallery slides and the gallery dots. Each keeps the handler it
	 *     already has, and the two anchors keep navigating natively, which is what makes the
	 *     image and the title still work once Boost's blanket handler is gone.
	 *   · The rest of the image column still navigates, by letting Boost's handler run, because
	 *     not every press on the picture reaches the anchor. Both overlays drawn over the photo —
	 *     Boost's label row and the theme's badge row — are `pointer-events: none` in
	 *     boost-plp.css, which hands their presses to `.boost-sd__product-image-wrapper`
	 *     underneath rather than to the anchor inside it; the mobile gallery is a sibling of the
	 *     anchor, not a child; and the dots strip and the wishlist button sit in the column below
	 *     the wrapper. Swallowing those would leave patches of the photo that do nothing, so the
	 *     whole column is left to Boost.
	 *   · Anything else is the info block, and is swallowed.
	 *
	 * Capture phase on `document`, because Boost's listener sits at or above the card and
	 * `stopPropagation` from here is what beats it — the same lever `onCardGalleryDragClick`
	 * above pulls. `preventDefault` is deliberately not called: a swallowed click has no default
	 * worth cancelling, and cancelling it would take the text selection this exists to allow.
	 */
	const CP_CARD_CONTROL =
		'a[href], button, input, select, textarea, label, [role="button"], [role="link"]';
	const CP_CARD_IMAGE = '.boost-sd__product-item-grid-view-layout-image';

	const onCardInfoClick = (event) => {
		const target = event.target;
		if (!target.closest?.('.boost-sd__product-item')) return;
		if (target.closest('.cp-plp-promo-item')) {
			event.stopPropagation();
			return;
		}
		if (target.closest(CP_CARD_CONTROL)) return;
		if (target.closest(CP_CARD_IMAGE)) return;

		event.stopPropagation();
	};

	const CP_PAGE_TOTAL = 'cp-plp-page-total';

	const labelPagination = () => {
		const pages = document.querySelector('.boost-sd__pagination-pages');
		if (!pages) return;

		const existing = pages.querySelector(`.${CP_PAGE_TOTAL}`);

		if (!MOBILE_PLP.matches) {
			existing?.remove();
			return;
		}

		const total = Math.max(
			0,
			...[...pages.querySelectorAll('.boost-sd__pagination-number')].map((node) =>
				Number.parseInt(node.textContent.trim(), 10)
			).filter(Number.isFinite)
		);
		// A single-page result has nothing to count towards, and the row is arrows-only anyway.
		if (total < 2) {
			existing?.remove();
			return;
		}

		const label = existing ?? document.createElement('span');
		if (!existing) {
			label.className = CP_PAGE_TOTAL;
			// Localized through the same bridge as the rest of the PLP copy; see `localizePanel`.
			label.dataset.cpI18n = 'plp_page_of';
			pages.append(label);
		}
		if (label.dataset.cpCount !== String(total)) label.dataset.cpCount = String(total);
	};

	/**
	 * A page turn lands the shopper on the first row of the new page, under the sticky header.
	 *
	 * Boost's own landing is the top of the *page*: once the next page has rendered,
	 * `DomHelper.scrollToTop` in bc-widget-integration.js runs `window.scrollTo({top: 0})`. On a
	 * collection page that is the hero banner and the promotion band — a full screen of things the
	 * shopper has already read — with the products they asked for out of view below.
	 *
	 * Boost exposes no target for that scroll. Its knobs are `window.boostSdDisableScrollToTop`,
	 * an opt-out flag read once at the top of `scrollToTop`, plus an element and a sticky offset
	 * that only the filter branch honours; the pagination branch scrolls to 0 regardless. So the
	 * flag is the hook. It is defined here as a getter, and the one read Boost makes while a page
	 * turn is pending is precisely the moment the new page is in the DOM and Boost is about to
	 * scroll: the getter takes that scroll to the listing instead and answers `true`, so Boost's
	 * does not run on top of it. Every other read — a filter click, the per-page limit — answers
	 * whatever the flag was set to, and Boost's behaviour there is untouched.
	 *
	 * Pending is set by a capture-phase click on a page control and expires on its own, so a fetch
	 * that fails (Boost never asks) cannot hand the scroll to the next filter click instead. The
	 * control is the pill, not the anchor inside it: Boost's handler is on the pill, and the anchor
	 * is only as wide as its text, so most clicks never reach it. The current page and a disabled
	 * end button carry no anchor at all (pagination-page-link.liquid), and Boost does nothing for
	 * them either.
	 */
	const PAGINATION_CONTROL = '.boost-sd__pagination-button, .boost-sd__pagination-number';
	const PAGE_TURN_TTL_MS = 10000;
	/* Figma: 40 between the header and the first row — the same landing the banner CTA uses. */
	const LISTING_GAP = 40;

	let pageTurnDeadline = 0;

	const onPaginationClick = (event) => {
		const control = event.target.closest?.(PAGINATION_CONTROL);
		if (!control?.querySelector('a[href]')) return;
		// A modified click opens the page in a new tab; nothing on this one is going to move.
		if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
		pageTurnDeadline = Date.now() + PAGE_TURN_TTL_MS;
	};

	/*
	 * `#boost-plp-listing` is the shell on collection pages (sections/boost-plp.liquid). Search
	 * results ship the same shell without the id, and the container is the floor for a widget
	 * placed some other way.
	 */
	const getListing = () =>
		document.getElementById('boost-plp-listing') ??
		document.querySelector('.boost-plp-shell, .boost-sd-container');

	/*
	 * The offset is the header's own height, not its rect: with `sticky="scroll-up"` it may not be
	 * pinned at the moment of measuring, only once the scroll towards it starts. And it is the
	 * *pinned* height by construction — the listing sits under a banner, so every landing this
	 * computes is well past the announcement bar. (collection-hero.liquid's CTA has to correct its
	 * landing after the fact because it jumps from the top, where the header is still 179 tall.)
	 */
	const stickyHeaderHeight = () => {
		const header = document.querySelector('.header-section:has(> #header-component[sticky])');
		return header ? header.offsetHeight : 0;
	};

	const listingScrollTop = (listing) =>
		Math.max(0, listing.getBoundingClientRect().top + window.scrollY - stickyHeaderHeight() - LISTING_GAP);

	const scrollToListing = () => {
		const listing = getListing();
		if (!listing) return false;
		window.scrollTo({
			top: listingScrollTop(listing),
			behavior: REDUCED_MOTION.matches ? 'instant' : 'smooth',
		});
		return true;
	};

	const hookBoostScrollToTop = () => {
		if (Object.getOwnPropertyDescriptor(window, 'boostSdDisableScrollToTop')?.get) return;
		// Whatever the merchant's own customization set stays the answer for every other read.
		let disabled = Boolean(window.boostSdDisableScrollToTop);
		Object.defineProperty(window, 'boostSdDisableScrollToTop', {
			configurable: true,
			get() {
				const pending = pageTurnDeadline !== 0 && Date.now() < pageTurnDeadline;
				pageTurnDeadline = 0;
				return pending && scrollToListing() ? true : disabled;
			},
			set(value) {
				disabled = Boolean(value);
			},
		});
	};

	/**
	 * On the search page every fetch Boost makes after the first paint — a page turn, a filter, a
	 * sort — has to go to its search endpoint, or the query is dropped: page 2 of "pompes" came
	 * back as the whole catalogue (2828 products instead of 107), and the header re-rendered
	 * with the no-search-term title.
	 *
	 * The first page is server-rendered through the right endpoint; the client-side refetch is
	 * `FilterService.getFilterApiEndpoint` in filter.module.js, which picks `/search` over
	 * `/filter` only on SSR `v2` stores with an `ssrUrl`. This store is `v1` with `ssrUrl` empty,
	 * so it always answers `filterUrl` — the collection endpoint, which ignores `q`. Pointing
	 * `filterUrl` at the configured `searchUrl` is the endpoint Boost would pick itself on v2.
	 *
	 * Only on the search template: an in-collection search (`/collections/…?q=`) is served by
	 * `/filter` with `incollection_search`, and must stay there. The URLs live on two objects —
	 * the global config, and the app config every registered block shares (the one
	 * filter.module reads) — and blocks register after this file runs, so this is a
	 * `decoratePlp` pass rather than a one-off at start.
	 */
	const SEARCH_PAGE_PATH = /\/search\/?$/;

	const routeSearchPageFetches = () => {
		if (!SEARCH_PAGE_PATH.test(window.location.pathname)) return;
		const integration = window.boostWidgetIntegration;
		if (!integration) return;
		const configs = [integration.config, ...Object.values(integration.blocks ?? {}).map((block) => block?.app)];
		for (const config of configs) {
			if (config?.searchUrl && config.filterUrl !== config.searchUrl) config.filterUrl = config.searchUrl;
		}
	};

	/**
	 * Figma shows five swatches and a "+5" where a product has more. product-swatch.liquid caps
	 * the row at that same five, but that template only takes effect once it has been pasted into
	 * Boost admin — so this stays the backstop for any row that reaches the DOM longer than that,
	 * and it hides rather than removes, so the count stays honest and Boost's own swatch click
	 * handlers keep working on the ones still in the DOM.
	 *
	 * Scoped to cards. The quick-view modal renders the same row class, and there the collapse is
	 * exactly wrong: the modal is the chooser the card's CTA sends the shopper to, so a value it
	 * hides behind a "+N" is a value nobody can pick. The modal is portalled to <body> rather
	 * than into a card, so the descendant selector is all it takes to leave it alone.
	 */
	const collapseSwatches = () => {
		for (const row of document.querySelectorAll(
			'.boost-sd__product-item .boost-sd__product-swatch-options'
		)) {
			if (row.classList.contains(PLP_DONE)) continue;

			const options = [...row.querySelectorAll('.boost-sd__product-swatch-option')];
			const overflow = options.length - PLP_SWATCH_LIMIT;

			// Marked either way: a row that does not overflow still must not be re-scanned.
			row.classList.add(PLP_DONE);
			if (overflow <= 0) continue;

			for (const option of options.slice(PLP_SWATCH_LIMIT)) {
				option.classList.add('boost-sd__product-swatch-option--cp-hidden');
			}

			const more = document.createElement('span');
			more.className = 'cp-plp-swatch-more';
			more.textContent = `+${overflow}`;
			row.append(more);
		}
	};

	/**
	 * Variant selection on the card — the swatch row is a chooser, not a colour preview.
	 *
	 * Boost renders the swatches and then binds nothing to them: clicking one checks the radio
	 * and that is the end of it — no image, no price, no CTA change (measured on the dev theme).
	 * Until this branch the "B4B - Variants Products" app was doing a partial version of that job
	 * from the outside, splitting each product into per-variant cards and rewriting their title,
	 * image and current price; its app embed is switched off in config/settings_data.json, so the
	 * behaviour lives here now, where the numbers it uses are the ones the card was rendered from.
	 *
	 * Everything a colour needs to say about itself rides on its radio as `data-cp-*`, published
	 * by boostai/src/product-swatch/product-swatch.liquid — the card itself carries no variants
	 * array, because product-item/main.liquid only serializes one when Boost admin's
	 * selectOptionBtn action is `quickAddToCart` and this store is on `popup`. Until that template
	 * is pasted into Boost admin the attributes are simply absent and every step below no-ops, so
	 * the two halves can land in either order.
	 *
	 * What a selection changes:
	 *   · the chip is checked, and CSS draws the ring off `:checked` (boost-plp.css);
	 *   · the CTA takes that colour's availability. Boost's own markup asks `product.available`,
	 *     which is true while *any* variant is in stock, so a product whose first colour is gone
	 *     was offering "Ajouter au panier" for something nobody can buy;
	 *   · the price, the compare-at and the discount pill take the colour's own figures;
	 *   · the card image and both product links follow the variant.
	 *
	 * What it deliberately does not change is what the button *does*: that comes from Boost
	 * admin (`productInfo.elements.selectOptionBtn.action`, set to popup here), so a multi-variant
	 * card still opens quick view and the variant is confirmed there. Only its label, its enabled
	 * state and the figures around it are restated.
	 */
	const withWidth = (url, width) => `${url}${url.includes('?') ? '&' : '?'}width=${width}`;

	const swapCardImage = (card, source) => {
		const image = card.querySelector('.boost-sd__product-image-img--main');
		if (!image || !source) return;

		const current = image.getAttribute('src') ?? '';
		if (current.split('?')[0] === source.split('?')[0]) return;

		/*
		 * The width list is Boost's own and differs between its mobile and desktop templates, so
		 * it is read back off the srcset that was rendered rather than repeated here — the card
		 * keeps whatever responsive ladder it was built with, pointed at the new file.
		 */
		const widths = (image.getAttribute('srcset') ?? '')
			.split(',')
			.map((candidate) => candidate.trim().split(/\s+/)[1])
			.filter(Boolean);

		const srcWidth = new URLSearchParams(current.split('?')[1] ?? '').get('width');
		image.setAttribute('src', srcWidth ? withWidth(source, srcWidth) : source);

		if (widths.length) {
			image.setAttribute(
				'srcset',
				widths.map((width) => `${withWidth(source, parseInt(width, 10))} ${width}`).join(', ')
			);
		}
	};

	/**
	 * Restate the price row for one variant.
	 *
	 * The compare-at and the pill are only in the DOM when the product was rendered on sale, so
	 * a colour that is on sale inside a product that was not needs them created — hence the
	 * class strings, which are copies of the ones boostai/src/product-price/main.liquid writes
	 * in its left/right layout, the only one this store uses.
	 */
	const setCardPrice = (card, rawPrice, rawCompare, rawDiscount) => {
		// A Recommendation widget's radios carry figures in the wrong notation (localizeCardPrices).
		const [price, compare, discount] = [rawPrice, rawCompare, rawDiscount].map(
			(text) => text && localizeMoney(text)
		);
		const amount = card.querySelector(
			'.boost-sd__product-price--sale, .boost-sd__product-price--default'
		);
		if (!amount || !price) return;

		const onSale = Boolean(compare);
		amount.classList.toggle('boost-sd__product-price--sale', onSale);
		amount.classList.toggle('boost-sd__product-price--default', !onSale);

		const setText = (node, text) => {
			if (node.textContent.trim() !== text) node.textContent = text;
		};

		setText(amount.querySelector('.boost-sd__format-currency') ?? amount, price);

		let compareNode = card.querySelector('.boost-sd__product-price--compare');
		if (onSale) {
			if (!compareNode) {
				compareNode = document.createElement('span');
				compareNode.className =
					'boost-sd__format-currency boost-sd__format-currency--price-compare boost-sd__product-price--compare';
				const inner = document.createElement('span');
				inner.className = 'boost-sd__format-currency';
				compareNode.append(inner);
				amount.after(compareNode);
			}
			setText(compareNode.querySelector('.boost-sd__format-currency') ?? compareNode, compare);
		} else {
			compareNode?.remove();
		}

		let pill = card.querySelector('.cp-plp-discount');
		if (onSale && discount) {
			if (!pill) {
				pill = document.createElement('span');
				pill.className = 'cp-plp-discount';
				pill.setAttribute('aria-hidden', 'true');
				(compareNode ?? amount).after(pill);
			}
			setText(pill, discount);
		} else {
			pill?.remove();
		}

		/*
		 * The visible spans are all aria-hidden, so this sentence is the whole price as far as a
		 * screen reader is concerned and cannot be left describing the colour we moved off.
		 */
		const spoken = card.querySelector('.boost-sd__product-price .boost-sd__visually-hidden');
		if (spoken) setText(spoken, spokenPrice(price, onSale ? compare : ''));
	};

	// A CTA that cannot add (sold out, in store only) and sends the shopper to the PDP instead.
	const CP_TO_PDP = 'cp-plp-atc--to-pdp';

	const setCardCta = (card, available) => {
		// Quote-only products never show stock state — their CTA opens the Centralio modal.
		// In-store-only products (custom.en_magasin_seulement) stay disabled whatever the variant.
		const button = card.querySelector(
			'.boost-sd__button--select-option:not(.boost-sd__button--soumission):not(.cp-plp-atc--in-store-only)'
		);
		if (!button) return;

		const label = available
			? button.dataset.cpAtcLabel
			: getStrings().plp_sold_out ?? button.dataset.cpSoldoutLabel;
		if (!label) return;

		// Sold out stays clickable and goes to the PDP instead (see onToProductPageClick).
		button.disabled = false;
		button.classList.toggle(CP_TO_PDP, !available);
		button.setAttribute('aria-label', label);

		for (const text of button.querySelectorAll('.boost-sd__cta-button-text')) {
			/*
			 * `data-cp-i18n` is what localizePanel keys off, and it runs on the same observer
			 * pass as this does — so it has to come *off* the in-stock label, or the next pass
			 * would put "Non disponible" straight back on a card that can be bought.
			 */
			if (available) text.removeAttribute('data-cp-i18n');
			else text.dataset.cpI18n = 'plp_sold_out';

			if (text.textContent.trim() !== label) text.textContent = label;
		}
	};

	const applyVariant = (input) => {
		const card = input.closest('.boost-sd__product-item');
		if (!card) return;

		for (const chip of card.querySelectorAll(
			'.boost-sd__product-swatch-option .boost-sd__radio-input'
		)) {
			const selected = chip === input;
			chip.checked = selected;
			chip.setAttribute('aria-checked', String(selected));
		}

		const {cpVariantId, cpAvailable, cpPrice, cpCompare, cpDiscount, cpImage} = input.dataset;

		/*
		 * Each of these is skipped when its attribute is missing, which covers two cases at
		 * once: the Boost template not being pasted into Boost admin yet, and an option value
		 * that matched no variant at all — neither of which is a reason to overwrite a card
		 * Boost rendered correctly.
		 */
		if (cpAvailable) setCardCta(card, cpAvailable !== 'false');
		setCardPrice(card, cpPrice, cpCompare, cpDiscount);
		swapCardImage(card, cpImage);

		if (!cpVariantId) return;
		for (const link of card.querySelectorAll('a.boost-sd__product-link')) {
			const url = new URL(link.href, location.origin);
			if (url.searchParams.get('variant') === cpVariantId) continue;

			url.searchParams.set('variant', cpVariantId);
			link.setAttribute('href', url.pathname + url.search);
		}
	};

	/**
	 * Seed each freshly rendered card from the chip the template shipped checked — its first —
	 * so the CTA, the price and the image describe a real variant from the first paint rather
	 * than the product as a whole. Marked per card, because Boost replaces the grid wholesale on
	 * every filter, sort and page change and the marker goes with the nodes it replaces.
	 */
	const syncCardVariants = () => {
		for (const card of document.querySelectorAll('.boost-sd__product-item')) {
			if (card.dataset.cpVariantSynced) continue;

			const chips = card.querySelectorAll(
				'.boost-sd__product-swatch-option .boost-sd__radio-input'
			);
			if (!chips.length) continue;

			card.dataset.cpVariantSynced = '1';
			applyVariant([...chips].find((chip) => chip.checked) ?? chips[0]);
		}
	};

	/**
	 * Keep the card on the colour the shopper picked.
	 *
	 * Boost previews an option value while the pointer sits on its chip — it rewrites the card
	 * image to that colour, then puts the product's own first image back when the pointer
	 * leaves. That restore knows nothing about a choice having been made, so a shopper who
	 * *clicks* the yellow chip watches the card fall back to beige the moment they move off the
	 * row: the selection was applied, and then undone by a handler that was only ever meant to
	 * clean up after a preview.
	 *
	 * The preview itself is worth keeping — it is how a shopper sees a colour without
	 * committing to it — so nothing here fights Boost while the pointer is still on the row.
	 * The selection is restated afterwards instead, which makes it the last write rather than a
	 * competing one. `:hover` is asked of the live DOM rather than tracked, so the guard costs a
	 * selector match and keeps no state to fall out of step.
	 *
	 * Only the image is restated. The price, the CTA and the links are set by `applyVariant` and
	 * nothing puts those back, so re-running the whole of it here would be work per pointer
	 * movement for no change.
	 */
	const reassertVariantImages = () => {
		for (const card of document.querySelectorAll('.boost-sd__product-item')) {
			if (card.querySelector('.boost-sd__product-swatch-options:hover')) continue;

			const selected = card.querySelector(
				'.boost-sd__product-swatch-option .boost-sd__radio-input:checked'
			);
			if (selected?.dataset.cpImage) swapCardImage(card, selected.dataset.cpImage);
		}
	};

	/*
	 * `mouseout` bubbles where `mouseleave` does not, so one delegated listener covers every card
	 * in the grid and survives Boost replacing all of them. Moving between two chips keeps
	 * `relatedTarget` inside the row and is ignored, which is what leaves Boost free to preview
	 * the chip being moved *to*.
	 *
	 * A frame is waited out because Boost's restore runs from its own pointer handler: restating
	 * the selection synchronously here would only be overwritten by it a moment later.
	 */
	const onSwatchPointerOut = (event) => {
		const row = event.target.closest?.('.boost-sd__product-swatch-options');
		if (!row || row.contains(event.relatedTarget)) return;

		requestAnimationFrame(reassertVariantImages);
	};

	const onSwatchChange = (event) => {
		const input = event.target.closest?.(
			'.boost-sd__product-swatch-option .boost-sd__radio-input'
		);
		if (input) applyVariant(input);
	};

	/*
	 * Boost gives the chip's label `tabindex="0"` but no key handling, so a keyboard reaches the
	 * swatches and can do nothing with them. A label is not a control — Enter and Space on one
	 * fire no click — so the selection is made here.
	 */
	const onSwatchKeydown = (event) => {
		if (event.key !== 'Enter' && event.key !== ' ') return;

		const label = event.target.closest?.(
			'.boost-sd__product-swatch-option .boost-sd__radio-label'
		);
		const input = label?.previousElementSibling;
		if (!input?.classList.contains('boost-sd__radio-input')) return;

		event.preventDefault();

		/*
		 * Inside the quick-view modal the selection is Boost's to make — it owns the `--selected`
		 * class and the add-to-cart state that follow from it — so the label is clicked and the
		 * rest follows the pointer path, as selectQuickViewOptions does. applyVariant is for
		 * cards and returns early anywhere else, so without this a keyboard reaching the modal's
		 * chips had its Enter swallowed and nothing done with it.
		 */
		if (label.closest('.boost-sd__product-item')) applyVariant(input);
		else label.click();
	};

	/**
	 * The Recommendation widget's carousel down to one card a slide on phones — the theme's
	 * 750, where the PLP grid goes to one column. The CSS then pads the slider's viewport so
	 * the next card peeks in from the right (Figma 2089-12398; boost-plp.css #c484).
	 *
	 * Boost builds the slick config in its runtime (`getMobileCarouselConfig`) with a hardcoded
	 * two cards under 768 and no setting to change it, and the carousel template never sees the
	 * config — so there is nothing to edit on the Boost side. slick leaves its instance on the
	 * track element, though, and takes an extra breakpoint after init: 750 is `max-width: 749px`,
	 * since slick applies a breakpoint while the window is narrower than it. The refresh rebuilds
	 * the infinite-mode clones, which is why this runs ahead of `scopeCarouselSwatches`; the
	 * breakpoint check keeps it to once per instance, as the rebuild lands back in the observer.
	 */
	const CAROUSEL_PHONE_BREAKPOINT = 750;

	/**
	 * The cross-sell placement: a Recommendation widget dropped into the PDP buy box (the
	 * widget id comes from the product's "Produits recommandés" metafield). It lists at most
	 * three products as stacked rows (Figma 1-199296 / 52-60095), so the carousel Boost always
	 * builds around it is taken down — slick's own `unslick` puts the original slides back and
	 * drops the clones, its arrows' handlers and the drag. boost-plp.css (#c496) already lays
	 * the slick markup out as rows, so nothing jumps in the frame before this runs. Ahead of
	 * `addCarouselPhoneBreakpoint` and `scopeCarouselSwatches`, so neither sees the carousel.
	 */
	const unslickCrossSell = () => {
		for (const track of document.querySelectorAll(
			'.product-details .boost-sd__recommendation .boost-sd__slider-container.slick-initialized'
		)) {
			track.slick?.unslick();
		}
	};

	/**
	 * The cross-sell rows' entrance (boost-plp.css #c506) plays once per row. Boost
	 * initialises slick on the widget a second time well after the first render, and the
	 * `unslickCrossSell` that follows moves the slides back out — and a CSS animation restarts
	 * whenever its element is put back in the document, so the rows faded in again while
	 * the shopper was already scrolling past. Marking the row when it finishes switches the
	 * animation off for it (#c507), so later moves leave it alone.
	 */
	const onCrossSellRowShown = (event) => {
		if (event.animationName === 'cp-xsell-row-in') event.target.dataset.cpXsellShown = '';
	};

	const addCarouselPhoneBreakpoint = () => {
		for (const track of document.querySelectorAll('.boost-sd__recommendation .boost-sd__slider-container.slick-initialized')) {
			const slick = track.slick;
			if (!slick || slick.options.responsive?.some(({breakpoint}) => breakpoint === CAROUSEL_PHONE_BREAKPOINT)) continue;

			slick.slickSetOption('responsive', [{breakpoint: CAROUSEL_PHONE_BREAKPOINT, settings: {slidesToShow: 1, slidesToScroll: 1}}], true);
		}
	};

	/**
	 * The Recommendation widget's cards — the "Bestsellers" carousel under the grid.
	 *
	 * Boost renders that widget from the same product-item templates as the grid, so every card
	 * pass above already reaches it. What it adds is *copies* of cards the page may already have,
	 * and a copy of a card is a copy of its swatch radios: the same `name`, which the browser
	 * treats as one group across the whole document, and the same `id`, which is what every
	 * chip's `<label for>` points at. Two sources of copies:
	 *
	 *   · The widget itself, whenever a product it recommends is also on the current grid page —
	 *     which, for a collection's bestsellers, is most of them.
	 *   · slick, which Boost runs in infinite mode: the wrap-around is built by cloning the
	 *     first and last slides — jQuery clones, attributes and all — onto either end of the
	 *     track, and it rebuilds them on its own when it crosses its 768 breakpoint.
	 *
	 * Left alone, two things go wrong. A card arrives with its first chip checked, and a checked
	 * radio joining a group unchecks the one already checked in it — so the moment the widget
	 * renders, the grid card for the same product loses its selection, and the ring, the price
	 * and the CTA that `applyVariant` draws off `:checked` have nothing to draw. And a tap on a
	 * chip inside a copy resolves its label to the *first* element with that id, which is the
	 * other card's radio, so the copy appears not to respond.
	 *
	 * So every card inside the widget gets a group of its own: the widget's id, plus slick's
	 * `data-slick-index` for a clone — negative for the copies ahead of the track, past the count
	 * for the ones after it, so unique per clone. Each radio remembers the `name` and `id` it was
	 * rendered with, and the card records the scope it was last given, so a clone that arrives
	 * carrying its original's attributes (jQuery copies `data-*` too) is recognised as being in
	 * the wrong scope and renamed from the base rather than skipped or suffixed twice.
	 *
	 * Then any card on the page left with no checked chip gets it back from the `aria-checked`
	 * attribute the template ships and `applyVariant` maintains: an attribute, so the group rule
	 * never touched it, and per chip, so the colour the shopper had picked is the one restored
	 * rather than the first. Page-wide rather than widget-wide, because the card the widget's
	 * arrival unchecked is the grid's.
	 *
	 * Runs before `syncCardVariants` in the same pass, so the seed it does on a fresh card finds
	 * the chip it expects checked.
	 */
	const scopeCarouselSwatches = () => {
		for (const card of document.querySelectorAll('.boost-sd__recommendation .boost-sd__product-item')) {
			const slide = card.closest('.slick-slide');
			const clone = slide?.classList.contains('slick-cloned') ? `-clone${slide.dataset.slickIndex ?? ''}` : '';
			const scope = `-${card.closest('.boost-sd__recommendation').id || 'recommendation'}${clone}`;
			if (card.dataset.cpSwatchScope === scope) continue;

			card.dataset.cpSwatchScope = scope;
			for (const input of card.querySelectorAll('.boost-sd__radio-input')) {
				input.dataset.cpBaseName ??= input.name;
				input.dataset.cpBaseId ??= input.id;
				input.name = input.dataset.cpBaseName + scope;
				input.id = input.dataset.cpBaseId + scope;

				// The label is the radio's next sibling (product-swatch.liquid) — `onSwatchKeydown` reads it the same way.
				const label = input.nextElementSibling;
				if (label instanceof HTMLLabelElement && label.htmlFor) label.htmlFor = input.id;
			}
		}

		for (const card of document.querySelectorAll('.boost-sd__product-item')) {
			if (card.querySelector('.boost-sd__product-swatch-option .boost-sd__radio-input:checked')) continue;

			const remembered = card.querySelector(
				'.boost-sd__product-swatch-option .boost-sd__radio-input[aria-checked="true"]'
			);
			if (remembered) remembered.checked = true;
		}
	};

	/**
	 * Promotion cards (Bloc Publicitaire) in the Boost grid — plan decision #4.
	 *
	 * The shell section (sections/boost-plp.liquid) renders every
	 * `custom.bloc_publicitaire_liste` entry server-side, complete with the metaobject's
	 * colours and hover animation, into the inert `<template id="cp-plp-promotions">`. This
	 * clones them into the top of `.boost-sd__product-list` whenever the grid is showing
	 * page 1 with no filter applied — the same rule the native grid applies at render time —
	 * and takes them back out when a filter or a page change makes the state stop qualifying.
	 *
	 * State is read from the URL because that *is* Boost's state: the widget pushes `page`
	 * and its `pf_*` filter params into the query string on every SPA transition, and the
	 * re-render that follows is what lands us back here through the MutationObserver. An
	 * in-collection search (`q`) counts as filtered for the same reason a `pf_*` param does:
	 * the shopper has narrowed the grid, and a promo card pretending to be a result there
	 * would be noise the native page never shows either.
	 *
	 * Idempotence and the two failure modes the plan names:
	 * · no duplicates — nothing is injected while a previous injection is still in the list
	 *   (Boost's full re-render wipes it, which is exactly when re-injection is wanted);
	 * · no orphans — a non-qualifying state removes whatever is there, so cards can never
	 *   survive into a filtered grid.
	 */
	const promoStateQualifies = () => {
		const params = new URLSearchParams(window.location.search);

		if ((params.get('page') ?? '1') !== '1') return false;
		if (params.get('q')) return false;

		for (const key of params.keys()) {
			if (key.startsWith('pf_')) return false;
		}
		return true;
	};

	const injectPromotions = () => {
		const template = document.getElementById('cp-plp-promotions');
		if (!template) return;

		const list = document.querySelector('.boost-sd__product-list');
		if (!list) return;

		if (!promoStateQualifies()) {
			for (const item of list.querySelectorAll('.cp-plp-promo-item')) item.remove();
			return;
		}

		if (list.querySelector('.cp-plp-promo-item')) return;

		const fragment = document.createDocumentFragment();
		// Only the cards: the snippet also leaves its own <script> tag in the template, which
		// must be neither wrapped as a grid item nor copied — the shell loads that script itself.
		for (const card of template.content.querySelectorAll('.promotion-card')) {
			const item = document.createElement('div');
			// Boost's own item classes, so its grid treats the card as one of its own.
			item.className =
				'boost-sd__product-item boost-sd__product-item--no-border boost-sd__product-item-grid-view-layout cp-plp-promo-item';

			item.append(card.cloneNode(true));
			fragment.append(item);
		}
		list.prepend(fragment);
	};

	/**
	 * The Variable Products app (`b4b-variable-products.js`) splits a product into per-variant
	 * cards and rewrites the card's title, image and *current* price client-side — but not the
	 * compare-at span or the discount pill, which the Boost template computed server-side from
	 * the product's min price. On a card whose displayed variant is not the cheapest one, that
	 * leaves "$1,299.99 ~~$1,299.99~~ −31%": the same figure struck through beside itself, with
	 * a percentage that belongs to a different variant.
	 *
	 * The app offers no hook at rewrite time, so this runs from the same MutationObserver its
	 * rewrites trigger, and simply compares the two figures as digit strings (currency symbols,
	 * spaces and separators stripped — equality is format-proof, which is all that is needed).
	 * Equal means "no real discount to show": a class on the row hides the compare-at and the
	 * pill and drops the sale colouring, in boost-plp.css. Re-evaluated — not just set — every
	 * pass, because the app rewrites again on variant swatch clicks.
	 */
	const fixVariantPriceRows = () => {
		for (const content of document.querySelectorAll(
			'.boost-sd__product-item .boost-sd__product-price-content'
		)) {
			const sale = content.querySelector('.boost-sd__product-price--sale');
			const compare = content.querySelector('.boost-sd__product-price--compare');
			if (!sale || !compare) continue;

			const digits = (el) => el.textContent.replace(/\D/g, '');
			content.classList.toggle('cp-plp-price-equal', digits(sale) === digits(compare));
		}
	};

	/**
	 * Name the house brand in the vendor filter.
	 *
	 * A Club Piscine own-brand product carries no vendor, and Boost buckets those under the
	 * literal string "None" — so the brand group reads "None (11)" and, once ticked, the chip
	 * reads "Marque : None". The product cards already substitute the brand name for that empty
	 * vendor (`boostai/src/product-item/product-info.liquid`); the filter has to agree with them.
	 *
	 * The substitution also exists in the Boost templates mirrored under `boostai/src/**`
	 * (`filter-option/filter-option-list-item.liquid`, `filter-tree/refineby.liquid`), which is
	 * where it belongs — done there it never paints "None" first. But those files are not part of
	 * a theme push: they only take effect once pasted into Boost admin. This pass is what makes
	 * the label correct from the theme alone, and it costs nothing once the paste lands, because
	 * by then it finds no "None" to rewrite.
	 *
	 * Scoped to the vendor group rather than run over every filter value: the option list is
	 * matched on its `<ul>` id, which Boost sets from the filter option id (`pf_p_vendor` and the
	 * like), and the chips on the filter key inside the `metadata` payload Boost writes for the
	 * clear action. A value labelled "None" in any other group is left alone.
	 *
	 * Runs from the same MutationObserver as the other PLP passes, since Boost re-renders the
	 * whole filter tree on every refinement. Idempotent: the rewritten node no longer matches.
	 */
	const EMPTY_VENDOR = 'None';
	const HOUSE_BRAND = 'Club Piscine';

	/** Boost upper-cases the values of a group with `displayAllValuesInUppercaseForm`. */
	const houseBrandFor = (text) => (text === text.toUpperCase() ? HOUSE_BRAND.toUpperCase() : HOUSE_BRAND);

	const isEmptyVendorText = (text) => text.toUpperCase() === EMPTY_VENDOR.toUpperCase();

	const nameHouseBrand = () => {
		for (const list of document.querySelectorAll('.boost-sd__filter-option-listitem')) {
			if (!list.id.toLowerCase().includes('vendor')) continue;

			for (const label of list.querySelectorAll('.boost-sd__filter-option-item-label')) {
				const text = label.textContent.trim();
				if (!isEmptyVendorText(text)) continue;

				const brand = houseBrandFor(text);
				label.textContent = brand;

				// The accessible name embeds the value too, so it has to move with the label —
				// otherwise the name no longer contains the visible text (WCAG 2.5.3).
				const button = label.closest('button');
				const name = button?.getAttribute('aria-label');
				if (name) button.setAttribute('aria-label', name.replace(text, brand));
			}
		}

		for (const chip of document.querySelectorAll('.boost-sd__refine-by-vertical-refine-by-item')) {
			/*
			 * Boost writes the clear action to `data-metadata`. Reading a bare `metadata` is why
			 * the chip kept saying "None": the attribute is not there on the markup Boost serves,
			 * so the vendor test never passed and every chip was skipped. The mirrored template
			 * had drifted to that bare spelling and has since been corrected to match, but both
			 * are still accepted here — the fallback costs one `??` and means a Boost admin still
			 * serving an older paste of it keeps working.
			 */
			const action = chip.getAttribute('data-metadata') ?? chip.getAttribute('metadata');
			if (!action?.toLowerCase().includes('vendor')) continue;

			const value = chip.querySelector('.boost-sd__refine-by-vertical-refine-by-value');
			const text = value?.textContent.trim();
			if (!text || !isEmptyVendorText(text)) continue;

			const brand = houseBrandFor(text);
			value.textContent = brand;

			const name = chip.getAttribute('aria-label');
			if (name) chip.setAttribute('aria-label', name.replace(text, brand));
		}
	};

	/**
	 * Translate Boost's filter facet *values* on the English storefront.
	 *
	 * A filter's own name resolves per locale in Boost admin — that is why "Color" and "Product
	 * Category" already read correctly above. But each filter's values print straight from the
	 * store's tag/metafield text, which carries no locale of its own, so swatches and options kept
	 * their French text on `/en/` forever. This cannot be fixed in the mirrored templates: both
	 * filter-option-list-item.liquid and filter-option-swatch-item.liquid get only `value.label` /
	 * `value.key` from Boost, never a translation of it.
	 *
	 * `plp_filter_values` (snippets/boost-search-i18n.liquid) maps the exact French source string
	 * to its English counterpart, looped out of the translation metaobjects — on the French
	 * storefront `label` resolves to the same French word, so the lookup below is a no-op there.
	 * Covers colours, stock status, the two product badges and the whole `product_type` taxonomy.
	 * Size is the exception: its values are numeric and per department, so the unit suffix is a
	 * mechanical swap instead (translateOptionValues, below).
	 *
	 * Nothing here is keyed to a particular filter — a value is translated because it is in the
	 * dictionary, whichever facet renders it. Adding a vocabulary means adding entries and a loop
	 * in the snippet, never a change in this function.
	 *
	 * Runs from the same MutationObserver pass as `nameHouseBrand`, for the same reason: Boost
	 * re-renders the filter tree on every refinement. Idempotent, since a value already translated
	 * no longer matches a dictionary key.
	 */
	const translateFilterValues = () => {
		const dictionary = getStrings().plp_filter_values;
		const groupLabels = getStrings().plp_filter_group_labels;
		if (!dictionary && !groupLabels) return;

		const patchAttrs = (el, text, translated) => {
			for (const attr of ['aria-label', 'title']) {
				const value = el.getAttribute(attr);
				if (value?.includes(text)) el.setAttribute(attr, value.replace(text, translated));
			}
		};

		if (dictionary) {
			for (const label of document.querySelectorAll('.boost-sd__filter-option-item-label')) {
				const text = label.textContent.trim();
				const translated = dictionary[text];
				if (!translated || translated === text) continue;

				label.textContent = translated;
				const button = label.closest('button');
				if (button) patchAttrs(button, text, translated);
			}
		}

		for (const chip of document.querySelectorAll('.boost-sd__refine-by-vertical-refine-by-item')) {
			if (dictionary) {
				const value = chip.querySelector('.boost-sd__refine-by-vertical-refine-by-value');
				const text = value?.textContent.trim();
				const translated = text && dictionary[text];
				if (translated && translated !== text) {
					value.textContent = translated;
					patchAttrs(chip, text, translated);
				}
			}

			// `refineByOption.data.label` ("Couleur:") is a separate untranslated field from the
			// option's own name, which the sidebar heading already resolves per locale.
			if (groupLabels) {
				const option = chip.querySelector('.boost-sd__refine-by-vertical-refine-by-option');
				const raw = option?.textContent.trim();
				const text = raw?.replace(/:$/, '');
				const translated = text && groupLabels[text];
				if (translated && translated !== text) {
					option.textContent = raw.endsWith(':') ? `${translated}:` : translated;
					patchAttrs(chip, text, translated);
				}
			}
		}

		// The quickview's option rows print "Couleur :" / "Taille :" from `swatch.label`
		// (boostai/src/quickview/main.liquid), which is the option name as Boost indexed it —
		// always the primary-locale French, whatever the storefront language. Same dictionary,
		// since these are the same option names the filter chips carry. The locale owns the
		// punctuation: French keeps its no-break space before the colon, English drops it. The
		// radios under the row carry the name too, in their `aria-label` ("Couleur: Blanc").
		if (groupLabels) {
			for (const title of document.querySelectorAll('#boost-sd__modal-quickview .boost-sd__product-swatch-title')) {
				const raw = title.textContent.trim();
				const text = raw.replace(/\s*:$/, '');
				const translated = text && groupLabels[text];
				if (!translated || translated === text) continue;

				title.textContent = raw.endsWith(':') ? `${translated}:` : translated;
				for (const radio of title.parentElement?.querySelectorAll('[role="radio"]') ?? []) {
					patchAttrs(radio, text, translated);
				}
			}
		}
	};

	/**
	 * The filter lists whose values are a plain vocabulary, safe to reorder alphabetically.
	 *
	 * Deliberately an allowlist rather than every list: price, percent-off, rating and size carry
	 * meaning in their order and have to keep the one Boost gives them.
	 */
	const SORTABLE_FILTER_LISTS = ['pf_pt_categorie_de_produit'];

	/** How many rows Boost showed before we expanded a list, keyed by filter id. */
	const collapsedRowCount = new Map();

	/** Filter ids the shopper has opened. Our state: Boost's own stays expanded throughout. */
	const openFilterLists = new Set();

	/** Set while we drive Boost's own toggle, so the interceptor below lets that click through. */
	let forcingExpand = false;

	const viewMoreButtonFor = (list) =>
		list
			.closest('.boost-sd__filter-option-itemwrapper')
			?.querySelector('.boost-sd__filter-option-viewmorebtn');

	/** Show the first `collapsedRowCount` rows, or all of them once the shopper opens the group. */
	const sliceFilterList = (list, id) => {
		const limit = collapsedRowCount.get(id) ?? 5;
		const open = openFilterLists.has(id);

		[...list.children].forEach((item, index) => {
			const display = open || index < limit ? '' : 'none';
			if (item.style.display !== display) item.style.display = display;
		});
	};

	/**
	 * Re-sort a translated filter list A–Z on the English storefront, and own its collapsed rows.
	 *
	 * Boost orders a filter's values alphabetically on the text it indexed, which is always the
	 * primary-locale French. So once `translateFilterValues` has swapped the labels, `/en` reads
	 * in French alphabetical order — "Wall Shelters, Water Heater Accessories, Cover Accessories"
	 * is Abris/Accessoires chauffe-eau/Accessoires couverts underneath. Boost cannot fix this at
	 * the source: it never indexes the English, which is the same reason the dictionary exists.
	 *
	 * Sorting alone only gets half of it. A collapsed group is a genuine slice — Boost renders
	 * five `<li>`s, not 287 behind a max-height — and it picks those five off the top of its own
	 * French order, so the rows are not merely misordered, they are the wrong rows: the true
	 * English A–E sit at French positions 172, 25, 26, 68 and 28 and are never in the DOM to
	 * sort. The only way to choose our own five is to make Boost render all of them, which is
	 * what the `button.click()` below does — client-side and free of network, since Boost already
	 * holds every value.
	 *
	 * From then on the group stays expanded as far as Boost is concerned and the collapsing is
	 * ours: `sliceFilterList` hides the overflow and the capture-phase listener swallows the
	 * shopper's click rather than letting Boost re-render its own five back over the top. That
	 * click changes inline styles only, which the shared observer does not watch, so the two
	 * passes that repaint the button are called directly.
	 *
	 * Runs after `translateFilterValues` in the same pass, on the swapped text rather than the
	 * French. Bails when a list is already in order, so the observer pass this triggers settles
	 * on the next turn instead of re-sorting forever.
	 */
	const sortFilterValues = () => {
		if (!isEnglishStorefront()) return;

		const labelOf = (item) =>
			item.querySelector('.boost-sd__filter-option-item-label')?.textContent.trim() ?? '';

		for (const id of SORTABLE_FILTER_LISTS) {
			// Boost renders each filter twice under the same id — once in the sidebar, once in
			// the drawer — and which copy holds the options rather than an empty shell flips
			// with the viewport, so `getElementById` would sort the wrong one below 992.
			for (const list of document.querySelectorAll(`[id="${id}"]`)) {
				if (!list.children.length) continue;

				const button = viewMoreButtonFor(list);
				const total = Number(button?.dataset.cpCount) || 0;
				const isSliced = [...list.children].some((item) => item.style.display === 'none');

				if (total && !isSliced && list.children.length < total) {
					// Boost is still showing its own slice. Remember its height, then open the
					// group once; the render that follows re-enters this pass through the
					// observer with every value in the DOM.
					collapsedRowCount.set(id, list.children.length);
					forcingExpand = true;
					button?.click();
					forcingExpand = false;
					continue;
				}

				if (button && button.dataset.cpSliceBound !== '1') {
					button.dataset.cpSliceBound = '1';
					button.addEventListener(
						'click',
						(event) => {
							if (forcingExpand) return;
							event.preventDefault();
							event.stopPropagation();

							if (openFilterLists.has(id)) openFilterLists.delete(id);
							else openFilterLists.add(id);

							sliceFilterList(list, id);
							syncViewMoreLabels();
							localizePanel();
						},
						true
					);
				}

				const items = [...list.children];
				const sorted = [...items].sort((a, b) =>
					labelOf(a).localeCompare(labelOf(b), 'en', {sensitivity: 'base', numeric: true})
				);
				if (!sorted.every((item, i) => item === items[i])) {
					for (const item of sorted) list.append(item);
				}

				sliceFilterList(list, id);
			}
		}
	};

	/**
	 * Flip a filter group's "Voir tout (n)" link to "Voir moins" once the group is open.
	 *
	 * Boost's own button toggles between its "View More" / "View Less" strings, but the templates
	 * relabel it through the `data-cp-i18n` bridge (Figma 3457-71320 wants the group's total in
	 * the label, which Boost's string does not carry), and `localizePanel` would write the
	 * collapsed wording straight back over Boost's open one. Boost leaves no class or attribute
	 * behind when it opens a group either — the only thing that changes is how many rows the
	 * list holds, so that is what decides the state here: a list showing every one of the
	 * group's values (`data-cp-count`) is open, anything shorter is collapsed. A group short
	 * enough that Boost never shows the link (it leaves the template's inline `display: none`
	 * in place) shows every value too, and is kept collapsed rather than read as open.
	 *
	 * Runs before `localizePanel` in the same pass, so the key swapped here is what that pass
	 * resolves. Also the natural place to give the toggle the `aria-expanded` / `aria-controls`
	 * pair Boost's button lacks, since the state is known here and nowhere else.
	 */
	const syncViewMoreLabels = () => {
		for (const button of document.querySelectorAll(
			'.boost-sd__filter-option-viewmorebtn[data-cp-count]'
		)) {
			const list = button
				.closest('.boost-sd__filter-option-itemwrapper')
				?.querySelector('.boost-sd__filter-option-listitem');
			if (!list) continue;

			// Counted on the rows actually showing, not on `children`: `sortFilterValues` leaves
			// every value in the DOM and hides the overflow itself, so a sliced list holds the
			// group's full count while looking collapsed. For a list Boost still slices the two
			// are the same number.
			const shown = [...list.children].filter((item) => item.style.display !== 'none').length;
			const total = Number(button.dataset.cpCount);
			const expanded = button.style.display !== 'none' && total > 0 && shown >= total;

			button.dataset.cpI18n = expanded ? 'plp_view_less' : 'plp_view_all';
			button.setAttribute('aria-expanded', String(expanded));
			if (list.id && button.getAttribute('aria-controls') !== list.id) {
				button.setAttribute('aria-controls', list.id);
			}
		}
	};

	/**
	 * Convert option values' French units and number formatting on the English storefront.
	 *
	 * Option values ("10 x 12 pi", "19,5 x 6 po", "50 000 BTU", "1,5 HP") are catalogued per
	 * department and far too numerous to enumerate value-by-value the way `translateFilterValues`
	 * does — but they are all a number and a unit, so a mechanical pass covers every one of them:
	 * the French unit abbreviation becomes its English one (Club Piscine's own table — "pi" is
	 * "ft.", "pi2" is "sq. ft.", "lb" is "lb.", and units that read the same in both languages,
	 * "kg", "BTU", "HP", "mm", are left alone), the decimal comma becomes a point and the space
	 * that groups French thousands becomes a comma. Scoped to the option filters' own lists
	 * (Boost's `pf_opt_*`, the `opt_` prefix is what marks a facet built from a variant option)
	 * and to the option chips a card or the quick-view modal draws, so text elsewhere on the
	 * page that happens to end in "pi" or "po" is never touched.
	 *
	 * The chips carry the same French option values: Boost indexes a variant's options in the
	 * store's primary locale only (its Translate & Adapt integration reaches titles, descriptions
	 * and product types, never a field "with variants"), so the quick view's "Size" row still
	 * listed "12 pi … 24 pi" on `/en/`. Only the text the shopper sees and the aria-label are
	 * converted — the radio's `value` and the label's `data-swatch-value` are what Boost and
	 * selectQuickViewOptions match against the variant's merged options, and those stay verbatim.
	 *
	 * Gated on `<html lang>` rather than routed through the `t`-backed dictionary the other passes
	 * use: it is a mechanical swap, not a per-string translation, so there is no French-locale
	 * identity entry to make it a no-op there on its own — without the lang check this would also
	 * relabel "pi"/"po" to "ft."/"in." for French shoppers.
	 *
	 * "h" is deliberately not in the table: the only option carrying it is Autonomie ("3 h",
	 * "4 h 30"), a duration, and the store's clock-time rule ("15 h" → "3 p.m.") would misread it.
	 *
	 * The lookahead also refuses a following "." — half the English forms end in one ("lb."), and
	 * without it a rule matches its own output on the next observer pass and grows a dot per pass,
	 * forever: a MutationObserver whose callback mutates never yields, and the tab hangs.
	 */
	const unitRule = (fr, en) => [new RegExp(`(?<!\\w)${fr.replace('/', '\\/')}(?![\\w²³.])`, 'g'), en];
	const OPTION_UNITS = [
		// Compound and exponent forms first, so the plain unit below never eats their suffix.
		['mi/h', 'mph'],
		['pi/s', 'f/s'],
		['pi²', 'sq. ft.'],
		['pi2', 'sq. ft.'],
		['po²', 'sq. in.'],
		['po2', 'sq. in.'],
		['vg²', 'sq. yd.'],
		['vg2', 'sq. yd.'],
		['pi³', 'cu. ft.'],
		['pi3', 'cu. ft.'],
		['po³', 'cu. in.'],
		['po3', 'cu. in.'],
		['pi', 'ft.'],
		['po', 'in.'],
		['vg', 'yd.'],
		['mi', 'mi.'],
		['lb', 'lb.'],
		['oz', 'oz.'],
		['gal', 'gal.'],
		['pt', 'pt.'],
		['qt', 'qt.'],
	].map(([fr, en]) => unitRule(fr, en));

	// A decimal comma is one followed by anything but a group of exactly three digits, so the
	// "50,000" the thousands rule writes is not read back as a decimal on the next observer
	// pass. The thousands rule only takes a space that sits before exactly three digits
	// ("50 000", "1 000 000"), so "4 h 30" and "10 x 12" keep their spaces.
	const OPTION_NUMBERS = [
		[/(\d),(?=\d)(?!\d{3}(?!\d))/g, '$1.'],
		[/(\d)[   ](?=\d{3}(?!\d))/g, '$1,'],
	];

	// A range value joins its two figures with "à" ("1,2 m à 3,6 m") or opens with "Jusqu'à"
	// ("Jusqu'à 25 x 50 pi", either apostrophe); only the connector that sits right before a
	// figure is taken, so an "à" in prose is never touched.
	const OPTION_RANGE = [
		[/(?<!\w)[Jj]usqu[’']à (?=\d)/g, 'Up to '],
		[/ à (?=\d)/g, ' to '],
	];

	const convertOptionValue = (text) =>
		[...OPTION_NUMBERS, ...OPTION_RANGE, ...OPTION_UNITS].reduce((result, [pattern, unit]) => result.replace(pattern, unit), text);

	const convertOptionValueAttr = (el, attr) => {
		const value = el.getAttribute(attr);
		if (!value) return;
		const converted = convertOptionValue(value);
		if (converted !== value) el.setAttribute(attr, converted);
	};

	const OPTION_CHIP_ROWS = '.boost-sd__product-item .boost-sd__product-swatch, #boost-sd__modal-quickview .boost-sd__product-swatch';

	const translateOptionValues = () => {
		if (document.documentElement.lang !== 'en') return;

		// Card and quick-view option chips. The label's text is the value itself and the tooltip
		// above a colour chip repeats it — both are text-only nodes, so a textContent write loses
		// nothing. Idempotent: once swapped, no rule matches its own output. A worded value
		// ("Petit à Moyen") is no number-and-unit; it resolves through the same `plp_filter_values`
		// dictionary the filter list uses, so one metaobject entry reaches every surface.
		const dictionary = getStrings().plp_filter_values ?? {};
		const convertChipText = (text) => dictionary[text.trim()] ?? convertOptionValue(text);
		for (const row of document.querySelectorAll(OPTION_CHIP_ROWS)) {
			for (const node of row.querySelectorAll('.boost-sd__radio-label, .boost-sd__tooltip-content')) {
				const text = node.textContent;
				const translated = convertChipText(text);
				if (translated !== text) node.textContent = translated;

				const label = node.getAttribute('aria-label');
				if (label) {
					const converted = dictionary[label.trim()] ?? convertOptionValue(label);
					if (converted !== label) node.setAttribute('aria-label', converted);
				}
			}
		}

		for (const list of document.querySelectorAll('.boost-sd__filter-option-listitem[id^="pf_opt_"]')) {
			for (const label of list.querySelectorAll('.boost-sd__filter-option-item-label')) {
				const text = label.textContent;
				const translated = convertOptionValue(text);
				if (translated === text) continue;

				label.textContent = translated;
				const button = label.closest('button');
				for (const attr of ['aria-label', 'title']) {
					const value = button?.getAttribute(attr);
					if (value) button.setAttribute(attr, convertOptionValue(value));
				}
			}
		}

		for (const chip of document.querySelectorAll('.boost-sd__refine-by-vertical-refine-by-item')) {
			const metadata = chip.getAttribute('data-metadata') ?? chip.getAttribute('metadata');
			if (!metadata?.toLowerCase().includes('pf_opt_')) continue;

			// Write only on a change: a textContent write replaces the text node even when the
			// string is identical, which is a childList mutation — the observer calling this pass
			// would re-fire on it forever, and the tab hangs as soon as a pf_opt_ chip is applied.
			const value = chip.querySelector('.boost-sd__refine-by-vertical-refine-by-value');
			if (value) {
				const text = value.textContent;
				const converted = convertOptionValue(text);
				if (converted !== text) value.textContent = converted;
			}

			convertOptionValueAttr(chip, 'aria-label');
		}
	};

	/**
	 * Draw the price filter's two figures in the shopper's number format, and without a $.
	 *
	 * Boost owns those two `<input>`s: it writes a bare whole number into each when the filter
	 * mounts or is applied, and it reads a bare number back on Enter and on blur — "1,600.50"
	 * is accepted, anything with a space or a decimal comma is silently dropped. So the value in
	 * the box can never be the French figure. The input keeps the raw number for Boost, and a
	 * span drawn over it (`cp-plp-range-figure`, boost-plp.css) shows what the shopper reads:
	 * `1 800,00` in French, `1,800.00` in English, and past a million `2 000 000 000,00` —
	 * the format the product cards use (boostai/src/product-price/main.liquid), minus the $.
	 * While the input has focus the overlay steps aside and the raw figure is edited directly.
	 * A French-typed separator is mapped as it is typed — the space dropped, the decimal comma
	 * made a point — in the capture phase of the `input` event, before Boost's own handler sees
	 * the keystroke. It has to be that early: Boost fills its state from every keystroke by
	 * stripping commas as thousands separators, and applies the box from that state, so
	 * "1700,5" left until blur is already 17005 by the time any later hook could read it.
	 * Enter and blur run a last normalisation as a backstop.
	 *
	 * The mirrored template (filter-option/filter-option-range/filter-option-range.liquid)
	 * flags the price filter with `data-cp-price-range` and drops the `$` unit span; until that
	 * paste lands the pass keys on the unit span instead, which the stylesheet hides.
	 *
	 * Boost writes the mounted value from its slider's init, after the input is in the DOM and
	 * after the observer pass that saw it arrive, so the pass repeats itself on the next frame.
	 */
	const PRICE_RANGE_INPUTS =
		'.boost-sd__filter-option-range-slider--container:is([data-cp-price-range], :has(.boost-sd__filter-option-range-slider-unit)) .boost-sd__filter-option-range-slider-input';

	const isEnglishStorefront = () => document.documentElement.lang === 'en';

	/** The number behind what a shopper may have typed, in either locale's notation. */
	const parseRangeFigure = (raw) => {
		const text = String(raw ?? '').trim();
		if (!text) return NaN;
		const normalized = isEnglishStorefront()
			? text.replace(/,/g, '')
			: text.replace(/[\s\u00a0\u202f]/g, '').replace(',', '.');
		return Number(normalized);
	};

	const formatRangeFigure = (raw) => {
		const number = parseRangeFigure(raw);
		if (!Number.isFinite(number)) return '';

		const english = isEnglishStorefront();
		const [whole, cents] = Math.abs(number).toFixed(2).split('.');
		const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, english ? ',' : ' ');
		return `${number < 0 ? '-' : ''}${grouped}${english ? '.' : ','}${cents}`;
	};

	const syncPriceRangeInputs = () => {
		for (const input of document.querySelectorAll(PRICE_RANGE_INPUTS)) {
			const field = input.parentElement;
			if (!field) continue;
			field.classList.add('cp-plp-range-field');

			let figure = field.querySelector('.cp-plp-range-figure');
			if (!figure) {
				figure = document.createElement('span');
				figure.className = 'cp-plp-range-figure';
				figure.setAttribute('aria-hidden', 'true');
				input.before(figure);
			}

			const text = formatRangeFigure(input.value);
			if (figure.textContent !== text) figure.textContent = text;
		}
	};

	/**
	 * The chip for an applied price range, "1234.5 - 23456", restated in the same format as the
	 * boxes: "1 234,50 - 23 456,00" in French, "1,234.50 - 23,456.00" in English, no $.
	 *
	 * Not done in filter-tree/refineby.liquid, though that template carries the same rewrite:
	 * the chips Boost actually serves are not drawn from it — they carry a
	 * removeFilterFromRefineBy action and none of the marks that template writes (its i18n
	 * bridge on the clear-all button, the id on each chip), so a paste there never reaches
	 * them. The figures are read from the chip's own metadata ("lower:upper"), never from its
	 * text, so the pass cannot misread its own output on the next observer run.
	 */
	const formatPriceRangeChips = () => {
		for (const chip of document.querySelectorAll('.boost-sd__refine-by-vertical-refine-by-item')) {
			const metadata = chip.getAttribute('data-metadata') ?? chip.getAttribute('metadata');
			if (!metadata) continue;

			let filter;
			try {
				filter = Object.values(JSON.parse(metadata).action ?? {})[0];
			} catch {
				continue;
			}
			if (filter?.type !== 'range' || !/pri(ce|x)/i.test(filter.key ?? '')) continue;

			const [rawLower, rawUpper] = String(filter.value ?? '').split(':');
			const lower = formatRangeFigure(rawLower);
			const upper = formatRangeFigure(rawUpper);
			if (!lower || !upper) continue;

			const value = chip.querySelector('.boost-sd__refine-by-vertical-refine-by-value');
			const text = `${lower} - ${upper}`;
			if (value && value.textContent.trim() !== text) value.textContent = text;

			const name = chip.getAttribute('aria-label');
			const rawName = `${rawLower} to ${rawUpper}`;
			if (name?.includes(rawName)) {
				chip.setAttribute('aria-label', name.replace(rawName, `${lower} to ${upper}`));
			}
		}
	};

	let priceRangeFrame = 0;
	const syncPriceRangeInputsSoon = () => {
		syncPriceRangeInputs();
		if (priceRangeFrame) return;
		priceRangeFrame = requestAnimationFrame(() => {
			priceRangeFrame = 0;
			syncPriceRangeInputs();
		});
	};

	/**
	 * Hand Boost a figure it can read. Runs in the capture phase of the two events Boost applies
	 * the box on, so the rewrite lands before its handler. The native setter plus an `input`
	 * event is what makes a React-controlled input take a programmatic value as if typed.
	 */
	const normalizePriceRangeInput = (input) => {
		const number = parseRangeFigure(input.value);
		if (!Number.isFinite(number)) return;

		const plain = String(number);
		if (input.value === plain) return;

		Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set.call(input, plain);
		input.dispatchEvent(new Event('input', {bubbles: true}));
	};

	const onPriceRangeKeydown = (event) => {
		if (event.key !== 'Enter') return;
		const input = event.target;
		if (input instanceof HTMLInputElement && input.matches(PRICE_RANGE_INPUTS)) {
			normalizePriceRangeInput(input);
		}
	};

	// `blur` rather than `focusout`: Boost applies the box from a blur listener on the input itself,
	// and blur is dispatched before focusout, so only a capture-phase blur lands ahead of it.
	const onPriceRangeBlur = (event) => {
		const input = event.target;
		if (input instanceof HTMLInputElement && input.matches(PRICE_RANGE_INPUTS)) {
			normalizePriceRangeInput(input);
			syncPriceRangeInputsSoon();
		}
	};

	/**
	 * Map the separators as they are typed. Only the characters are rewritten, never the number,
	 * so a trailing "1800." survives long enough for the cents to follow it. The caret is put
	 * back where it was, less the characters dropped ahead of it, since the native setter parks
	 * it at the end.
	 */
	const onPriceRangeInput = (event) => {
		const input = event.target;
		if (!(input instanceof HTMLInputElement) || !input.matches(PRICE_RANGE_INPUTS)) return;

		const raw = input.value;
		const mapChar = (char) => (/[\s\u00a0\u202f]/.test(char) ? '' : char === ',' ? (isEnglishStorefront() ? '' : '.') : char);
		const caret = input.selectionStart ?? raw.length;
		const before = [...raw.slice(0, caret)].map(mapChar).join('');
		const cleaned = before + [...raw.slice(caret)].map(mapChar).join('');
		if (cleaned === raw) return;

		Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set.call(input, cleaned);
		input.setSelectionRange(before.length, before.length);
	};

	/**
	 * Close the sort panel from the button boostai/src/toolbar/sorting.liquid puts in its title
	 * row (Figma 2094-10327). Boost ships no close control and owns whether the panel is open,
	 * so this forwards the click to the control that opened it — the same toggle a second click
	 * up there would use. Stripping `boost-sd__sorting-list--active` directly would leave Boost
	 * still believing the panel was down and swallow the shopper's next click on the button.
	 *
	 * Delegated on `document` and registered once, so it survives every Boost re-render.
	 */
	const onSortingCloseClick = (event) => {
		const close = event.target.closest?.('.cp-plp-sorting-close');
		if (!close) return;

		close.closest('.boost-sd__sorting')?.querySelector('.boost-sd__sorting-button')?.click();
	};

	/**
	 * Open quick view on the variant the card was showing (2089-10366).
	 *
	 * The modal prices the *product*, not a variant: boostai/src/quickview/main.liquid draws its
	 * row from `product.price_min` / `product.compare_at_price_min`, and ships every option row
	 * `checked` on its first value. So a card sitting on a $879.99 colour opened a modal quoting
	 * the cheapest variant in the product, with a size nobody chose lit up beside it.
	 *
	 * It cannot be fixed in that template. Boost's quick-view action takes a product and nothing
	 * else — `"quickView": { "productId": … }` in product-item/quickview-button.liquid, with
	 * `variant_id` existing only on the `quickAddToCart` action this store is not on — and the
	 * button is rendered long before the shopper picks a colour, so the variant does not exist at
	 * render time. The selection has to be carried across the open, client-side, which is the
	 * same division of labour hydrateQuickViewWishlist below already uses.
	 *
	 * Two writes, in this order:
	 *   · the modal's own chips are *clicked*, not just checked, so Boost's runtime does its own
	 *     variant resolution — it owns the `--selected` class on these labels (boost-plp.css) and
	 *     the image and add-to-cart state that follow from it. Driving its chips keeps it in step
	 *     instead of fighting it;
	 *   · the price row is then restated from the figures the card was already displaying.
	 *
	 * Those figures are the formatted strings `data-cp-price` / `data-cp-compare` /
	 * `data-cp-discount` that boostai/src/product-swatch/product-swatch.liquid published on the
	 * chip, rather than anything recomputed here: they came out of the same Liquid
	 * `format_currency` the card and the modal are both drawn with, so the two surfaces cannot
	 * disagree about a figure, a separator or a rounded percentage.
	 *
	 * Every step is skipped when its input is missing — a card with no colour chips, or a Boost
	 * template not yet pasted into Boost admin — so this never overwrites a modal Boost rendered
	 * correctly on its own.
	 */
	let pendingQuickViewVariant = null;

	/*
	 * Capture phase, because Boost's own handler on the same click is what tears the grid down to
	 * build the modal: by the bubble phase the card whose chip we need may already be gone.
	 */
	const rememberQuickViewVariant = (event) => {
		const trigger = event.target.closest?.(
			'.boost-sd__button--quick-view, .boost-sd__button--select-option'
		);
		if (!trigger) return;

		const chip = trigger
			.closest('.boost-sd__product-item')
			?.querySelector('.boost-sd__product-swatch-option .boost-sd__radio-input:checked');

		const {cpVariantId, cpPrice, cpCompare, cpDiscount} = chip?.dataset ?? {};

		/*
		 * Cleared rather than left standing when this card has nothing to say, so a stash can
		 * never be applied to the next modal opened from a different card.
		 */
		pendingQuickViewVariant = cpVariantId
			? {id: cpVariantId, price: cpPrice, compare: cpCompare, discount: cpDiscount}
			: null;
	};

	/**
	 * The value this variant carries for one option row.
	 *
	 * `original_merged_options` is the untruncated "Name:Value" list and is checked first.
	 * `merged_options` looks the same shape but is not a safe stand-in for it: Boost folds a
	 * multi-word swatch value down to a shared family label there — "Gris foncé" and "Gris pâle"
	 * both come back as "couleur:Gris" — which is fine for the family-level colour filter but
	 * means matching a variant on it picks the first one alphabetically regardless of which chip
	 * the shopper pressed. It only backstops a payload where `original_merged_options` is
	 * missing. The remaining two fallbacks are positional, for a payload shaped differently than
	 * the one measured here: `options` is the values in option order, and a variant title is
	 * those same values joined — the modal renders one row per entry of `options_with_values`, in
	 * order, so an index is a safe last resort.
	 */
	const optionValueFor = (variant, option, index) => {
		const name = option?.name;
		const merged = [
			...(Array.isArray(variant.original_merged_options) ? variant.original_merged_options : []),
			...(Array.isArray(variant.merged_options) ? variant.merged_options : []),
		];
		if (name && merged.length) {
			const prefix = `${name}:`;
			const entry = merged.find((pair) => pair.startsWith?.(prefix));
			if (entry) return entry.slice(prefix.length).trim();
		}

		if (Array.isArray(variant.options)) return String(variant.options[index] ?? '').trim();

		return String(variant.title ?? '').split(' / ')[index]?.trim() ?? '';
	};

	/**
	 * The value one swatch row is currently on.
	 *
	 * `--selected` is asked first, not `:checked` (2026-09-15): the static markup ships every
	 * option row with only its first value's input checked, and nothing in the modal moves that
	 * attribute afterwards — Boost's own runtime tracks the live pick entirely through which
	 * label carries `--selected`, the same class the CSS keys the pill and swatch-ring styling
	 * on. Reading `:checked` instead answers with the row's first value forever, including on
	 * the modal's very first paint, whenever that is not also the variant Boost resolved as the
	 * default. `:checked` is kept as a fallback for a row this class does not reach.
	 */
	const quickViewRowValue = (row) =>
		row.querySelector('.boost-sd__radio-label--selected')?.dataset.swatchValue ??
		row.querySelector('.boost-sd__radio-input:checked')?.value ??
		'';

	const selectQuickViewOptions = (modal, variantId) => {
		let product = null;
		try {
			product = JSON.parse(modal.dataset.product ?? '');
		} catch {
			// A blob Boost wrote in a shape we do not know is not worth guessing at.
			return;
		}

		const variant = product?.variants?.find((item) => String(item.id) === String(variantId));
		if (!variant) return;

		const options = product.options_with_values ?? [];

		[...modal.querySelectorAll('.boost-sd__product-swatch')].forEach((row, index) => {
			const value = optionValueFor(variant, options[index], index);
			if (!value) return;

			const label = [...row.querySelectorAll('.boost-sd__radio-label')].find(
				(node) => node.dataset.swatchValue === value
			);
			/* Already the chosen one — clicking a selected radio fires no change event anyway. */
			if (!label || label.classList.contains('boost-sd__radio-label--selected')) return;

			label.click();
		});
	};

	/**
	 * Restate the modal price row.
	 *
	 * The compare-at and the pill only exist in the DOM when the product was rendered on sale, so
	 * a variant on sale inside a product that was not needs them created. The markup is
	 * deliberately flat — three sibling spans, one class each — because that is the shape
	 * boostai/src/quickview/main.liquid writes, and its comment there records why: Boost's parser
	 * rewrites nested markup in this template group.
	 *
	 * The visible spans are all `aria-hidden`, so the hidden sentence is the whole price as far as
	 * a screen reader is concerned and cannot be left describing the variant we moved off.
	 */
	const setQuickViewPrice = (row, price, compare, discount) => {
		const amount = row.querySelector(
			'.boost-sd__product-price--sale, .boost-sd__product-price--default'
		);
		if (!amount || !price) return;

		const onSale = Boolean(compare);
		amount.classList.toggle('boost-sd__product-price--sale', onSale);
		amount.classList.toggle('boost-sd__product-price--default', !onSale);

		const setText = (node, text) => {
			if (node.textContent.trim() !== text) node.textContent = text;
		};

		setText(amount, price);

		/*
		 * Boost also mounts a second, native compare-at span of its own next to the flat one this
		 * template writes — `boost-sd__format-currency--price-compare` — but
		 * `#boost-sd__modal-quickview .boost-sd__format-currency.boost-sd__format-currency--price-compare`
		 * keeps it `display: none` unconditionally, so whatever text it carries is never seen or
		 * read (display:none drops it from the accessibility tree too). It is Boost's to manage
		 * and is left alone; only the flat `boost-sd__product-price--compare` this file owns is
		 * read, written or removed here.
		 */
		let compareNode = row.querySelector('.boost-sd__product-price--compare');
		if (onSale) {
			if (!compareNode) {
				compareNode = document.createElement('span');
				compareNode.className = 'boost-sd__product-price--compare';
				compareNode.setAttribute('aria-hidden', 'true');
				amount.after(compareNode);
			}
			setText(compareNode, compare);
		} else {
			compareNode?.remove();
		}

		let pill = row.querySelector('.cp-plp-discount');
		if (onSale && discount) {
			if (!pill) {
				pill = document.createElement('span');
				pill.className = 'cp-plp-discount';
				pill.setAttribute('aria-hidden', 'true');
				(compareNode ?? amount).after(pill);
			}
			setText(pill, discount);
		} else {
			pill?.remove();
		}

		const spoken = row.querySelector('.boost-sd__visually-hidden');
		if (spoken) setText(spoken, spokenPrice(price, onSale ? compare : ''));
	};

	/**
	 * The variant the modal's chips currently describe.
	 *
	 * The rows are read in the order boostai/src/quickview/main.liquid draws them — one per entry
	 * of `options_with_values`, in order — so the positional pairing `optionValueFor` already
	 * relies on holds in the other direction too, and the variant is the one whose value for
	 * every row is the value that row has checked.
	 *
	 * Null whenever the answer would be a guess: a payload we cannot parse, a row with nothing
	 * checked, or a combination no variant in the blob carries. Nothing downstream writes on a
	 * null, so an unreadable modal keeps whatever Boost and the template put in it.
	 */
	const selectedQuickViewVariant = (modal) => {
		let product = null;
		try {
			product = JSON.parse(modal.dataset.product ?? '');
		} catch {
			return null;
		}

		const variants = product?.variants ?? [];
		if (!variants.length) return null;

		/* A product whose only variant is `Default Title` renders no option rows at all. */
		const rows = [...modal.querySelectorAll('.boost-sd__product-swatch')];
		if (!rows.length) return variants.length === 1 ? variants[0] : null;

		const options = product.options_with_values ?? [];
		const picks = rows.map((row, index) => ({
			option: options[index],
			index,
			value: quickViewRowValue(row),
		}));
		if (picks.some((pick) => !pick.value)) return null;

		return (
			variants.find((variant) =>
				picks.every((pick) => optionValueFor(variant, pick.option, pick.index) === pick.value)
			) ?? null
		);
	};

	/**
	 * The modal's figures, in the page language (2026-09-21 screenshot).
	 *
	 * boostai/src/quickview/main.liquid prints every price in the shop format — $1,799.99 —
	 * whatever the page language, because the render it comes out of has no locale to test: the
	 * modal is not rendered with the grid but fetched on its own from Boost's quickview endpoint,
	 * and `request.current_locale` is blank there. Measured on /en/ while that template still
	 * rewrote to French the way the card templates do: the modal came back 1 799,99$ beside a
	 * card reading $1,799.99. So the rewrite product-price/main.liquid and
	 * product-swatch/product-swatch.liquid make in Liquid is made here for the modal, from the
	 * page's own `lang` — the same rule, character for character: thousands grouped by a space
	 * and the $ trailing in French, by a comma and the $ leading in English, and the cents
	 * dropped when they are .00 and kept otherwise ($1,800 but $7.50 — the 2026-09-21 rule).
	 *
	 * Every figure the template prints is covered — the row and the per-variant list beneath it.
	 * The list is rewritten in place, ahead of `syncQuickViewPrice` reading it, so the row it
	 * restates from there is localized already. The percentage pill carries no money and passes
	 * through untouched; a dollar pill ("-$5.00") is a money string with a sign.
	 *
	 * Either notation is read, so a modal rendered from an older paste of the template that still
	 * rewrote to French is corrected the same way: the decimal separator is whichever of `.`/`,`
	 * is followed by exactly two digits at the end, a figure with no such tail is whole dollars,
	 * and every other separator is grouping. Idempotent, which the observer pass relies on: a
	 * string already in the page notation is parsed and reprinted unchanged.
	 */
	const localizeMoney = (text) => {
		const raw = String(text ?? '');
		if (!raw.includes('$')) return raw;

		/*
		 * Every figure in the string, so a price range ("34,99$ - 59,99$") is rewritten whole. A
		 * sign only counts when it touches the figure ("-$5", "-5$"), so the dash of a range is
		 * not read as a minus; a run of digits with no $ beside it ("-29%") is left as it is.
		 */
		return raw.replace(
			/(-?)(\$)?(\d[\d\s\u00a0\u202f.,]*\d|\d)(\$)?/g,
			(whole, sign, before, amount, after) => {
				if (!before && !after) return whole;

				const digits = amount.replace(/[\s\u00a0\u202f]/g, '');
				const cents = digits.match(/[.,](\d{2})$/);
				const units = (cents ? digits.slice(0, -3) : digits).replace(/[.,]/g, '');
				const number = Number(`${units}.${cents ? cents[1] : '00'}`);
				if (!Number.isFinite(number)) return whole;

				const figure = formatRangeFigure(number).replace(/[.,]00$/, '');
				return isEnglishStorefront() ? `${sign}$${figure}` : `${sign}${figure}$`;
			}
		);
	};

	/**
	 * The hidden sentence that is a Boost price row as far as a screen reader is concerned \u2014
	 * every visible figure beside it is `aria-hidden`. product-price/main.liquid and
	 * quickview/main.liquid write it in English in every locale ("Regular price 34,99$" on the
	 * French site, with ", saving Save 140$" tacked on for a sale), so it is restated here from
	 * the theme's own content.price* strings (snippets/boost-search-i18n.liquid), sale price
	 * first as it is read on screen.
	 */
	const spokenPrice = (price, compare) => {
		const strings = getStrings();
		if (!compare) return `${strings.plp_price ?? 'Price'} ${price}`;
		return `${strings.plp_price_sale ?? 'Sale price'} ${price}. ${strings.plp_price_regular ?? 'Regular price'} ${compare}`;
	};

	/**
	 * Card prices in the page language, and the sentence a screen reader gets for them.
	 *
	 * The grid formats its figures in Liquid off `request.current_locale`, but the
	 * Recommendation widgets \u2014 the carousel under the PDP and the cross-sell rows in its buy box
	 * \u2014 come out of a render where that locale is blank, the same as the quick view's: measured
	 * on /en/ the widget read 34,99$ and 469,99$ beside an English page. Those templates cannot
	 * be re-saved in Boost admin (the compiled recommendation entry is over its 64K limit), so
	 * the figures are reprinted here by `localizeMoney`, which leaves an already-correct grid
	 * card untouched. The swatch radios' `data-cp-*` figures come from the same render; they
	 * are localized as `setCardPrice` reads them.
	 */
	const localizeCardPrices = () => {
		for (const row of document.querySelectorAll('.boost-sd__product-item .boost-sd__product-price')) {
			for (const node of row.querySelectorAll(
				'.boost-sd__format-currency:not(:has(.boost-sd__format-currency)), .cp-plp-discount'
			)) {
				const text = node.textContent.trim();
				const localized = localizeMoney(text);
				if (localized !== text) node.textContent = localized;
			}

			const spoken = row.querySelector('.boost-sd__visually-hidden');
			const amount = row.querySelector('.boost-sd__product-price--sale, .boost-sd__product-price--default');
			if (!spoken || !amount) continue;

			/* fixVariantPriceRows hides a compare-at equal to the price; it is not said either. */
			const compare = row.querySelector('.boost-sd__product-price--compare');
			const shown = compare && !compare.closest('.cp-plp-price-equal');
			const text = spokenPrice(amount.textContent.trim(), shown ? compare.textContent.trim() : '');
			if (spoken.textContent.trim() !== text) spoken.textContent = text;
		}
	};

	const localizeQuickViewPrices = (modal) => {
		for (const figures of modal.querySelectorAll('.cp-qv-variant-prices [data-cp-variant-id]')) {
			for (const key of ['cpPrice', 'cpCompare', 'cpDiscount']) {
				const text = figures.dataset[key];
				if (!text) continue;
				const localized = localizeMoney(text);
				if (localized !== text) figures.dataset[key] = localized;
			}
		}

		const row = modal.querySelector('.boost-sd__quick-view-price');
		if (!row) return;

		let changed = false;
		for (const node of row.querySelectorAll(
			'.boost-sd__product-price--sale, .boost-sd__product-price--default, .boost-sd__product-price--compare, .cp-plp-discount'
		)) {
			const text = node.textContent.trim();
			const localized = localizeMoney(text);
			if (localized === text) continue;
			node.textContent = localized;
			changed = true;
		}
		if (!changed) return;

		/* The same sentence setQuickViewPrice writes, restated from the spans just rewritten. */
		const spoken = row.querySelector('.boost-sd__visually-hidden');
		const amount = row.querySelector('.boost-sd__product-price--sale, .boost-sd__product-price--default');
		const compare = row.querySelector('.boost-sd__product-price--compare');
		if (spoken && amount) {
			spoken.textContent = spokenPrice(amount.textContent.trim(), compare?.textContent.trim());
		}
	};

	/**
	 * Price the modal row for whatever its chips now say (2089-10366).
	 *
	 * Boost's quick-view runtime resolves the variant behind those chips — it is what flips the
	 * add-to-cart button to "Épuisé" — but it never restates this row, because the row is not
	 * its markup: boostai/src/quickview/main.liquid writes it, for the reason recorded there. So
	 * a shopper switching size inside the modal watched the button change and the figure stay put.
	 *
	 * The figures are the formatted strings that template published, one empty span per variant,
	 * rather than anything recomputed from the numbers in the product blob: they came out of the
	 * same Liquid `format_currency` the row itself was drawn with, so the modal, the card and
	 * this list cannot disagree about a figure, a separator or a rounded percentage.
	 *
	 * Returns whether it could, so a caller holding a second-best set of figures knows whether
	 * they are still needed.
	 */
	const syncQuickViewPrice = (modal) => {
		localizeQuickViewPrices(modal);

		const row = modal.querySelector('.boost-sd__quick-view-price');
		if (!row) return false;

		const variant = selectedQuickViewVariant(modal);
		if (!variant) return false;

		const figures = [
			...modal.querySelectorAll('.cp-qv-variant-prices [data-cp-variant-id]'),
		].find((node) => node.dataset.cpVariantId === String(variant.id));
		/* No list at all is the Boost template not yet pasted into Boost admin. */
		if (!figures?.dataset.cpPrice) return false;

		const {cpPrice, cpCompare, cpDiscount} = figures.dataset;
		setQuickViewPrice(row, cpPrice, cpCompare, cpDiscount);
		return true;
	};

	/**
	 * The same, for the pass the observer makes: the modal opens with every option row checked on
	 * its first value, which is a combination nobody chose and whose price is not `price_min`
	 * unless the cheapest variant happens to be the one those first values name. Cheap to repeat,
	 * because `setQuickViewPrice` only touches a node whose text would change, so a modal already
	 * priced correctly produces no mutation and cannot feed the observer that called this.
	 */
	const syncQuickViewPriceRows = () => {
		const modal = document.querySelector('#boost-sd__modal-quickview');
		if (modal) syncQuickViewPrice(modal);
	};

	/**
	 * Greys out whatever combination the modal's chips do not carry an available variant for
	 * (2026-09-15 screenshot).
	 *
	 * Boost flips the add-to-cart button to "Épuisé" off the resolved variant, but it never
	 * revisits the chips themselves when a pick in one row turns the combination the other row
	 * now describes into one with no stock — a size pressed with a colour that has none of it
	 * left keeps its plain, sellable pill right through the button going grey, `--sold-out` never
	 * lands on it. Boost also does not add `--sold-out` for a value that has no stock in *any*
	 * colour, even on the modal's first paint; measured on this store's live modal, not assumed
	 * from the template.
	 *
	 * So this owns `--sold-out` in the modal outright rather than trusting Boost to have set it:
	 * for every value in every row, a variant is looked up carrying that value plus whatever the
	 * *other* rows are currently checked to, and the chip is greyed unless that variant exists and
	 * is `available`. Nothing here reads the class before writing it.
	 */
	const syncQuickViewAvailability = (modal) => {
		let product = null;
		try {
			product = JSON.parse(modal.dataset.product ?? '');
		} catch {
			return;
		}

		const variants = product?.variants ?? [];
		const options = product?.options_with_values ?? [];
		const rows = [...modal.querySelectorAll('.boost-sd__product-swatch')];
		if (!variants.length || !rows.length) return;

		rows.forEach((row, rowIndex) => {
			const otherPicks = rows
				.map((otherRow, otherIndex) => ({
					option: options[otherIndex],
					index: otherIndex,
					value: quickViewRowValue(otherRow),
				}))
				.filter((pick) => pick.index !== rowIndex);

			[...row.querySelectorAll('.boost-sd__radio-label')].forEach((label) => {
				const value = label.dataset.swatchValue;
				if (!value) return;

				const picks = [...otherPicks, {option: options[rowIndex], index: rowIndex, value}];
				/* An unfinished pick in another row (nothing checked yet) is not this label's
				 * fault to report, so it is left as sellable until the shopper finishes choosing. */
				const soldOut =
					picks.every((pick) => pick.value) &&
					!variants.some(
						(variant) =>
							variant.available &&
							picks.every((pick) => optionValueFor(variant, pick.option, pick.index) === pick.value)
					);

				label.classList.toggle('boost-sd__radio-label--sold-out', soldOut);
			});
		});
	};

	/**
	 * The same, for the pass the observer makes, for the reason `syncQuickViewPriceRows` makes
	 * one: the modal's first paint is itself a combination nobody chose.
	 */
	const syncQuickViewAvailabilityRows = () => {
		const modal = document.querySelector('#boost-sd__modal-quickview');
		if (modal) syncQuickViewAvailability(modal);
	};

	/*
	 * Boost's runtime disables the quick view CTA on a sold-out variant and writes its own
	 * "Sold out" / "Épuisé" into it. The theme says "Non disponible" / "Not available" there, as
	 * the cards do (`setCardCta`), so the label is handed to the i18n bridge while the button is
	 * disabled, and taken back off when Boost re-enables it, so its "Add to cart" stands.
	 */
	const syncQuickViewSoldOutLabel = () => {
		const button = document.querySelector('#boost-sd__modal-quickview .boost-sd__btn-add-to-cart');
		const text = button?.querySelector('.boost-sd__button-text');
		if (!text) return;

		if (button.disabled) {
			if (text.dataset.cpI18n !== 'plp_sold_out') text.dataset.cpI18n = 'plp_sold_out';
		} else if (text.hasAttribute('data-cp-i18n')) {
			text.removeAttribute('data-cp-i18n');
		}
	};

	/**
	 * Point the modal's quote CTA at the variant its chips now name.
	 *
	 * A quote product with several variants is the one case where the card cannot open the
	 * Centralio form itself: the form is asked about a single SKU, and the card has no way to
	 * say which of them the shopper meant. So its CTA opens the quick view instead
	 * (boostai/src/product-item/add-to-cart-button.liquid) and the modal carries the quote
	 * button, which is only worth pressing once this has told it what the chips settled on.
	 *
	 * The SKUs are published one span per variant by boostai/src/quickview/main.liquid rather
	 * than read out of the product blob on the modal shell, for the reason the price figures
	 * beside them are: they come out of Boost's own Liquid, which runs in that template and
	 * nowhere else, so nothing here depends on how Boost encodes that attribute or on whether it
	 * carries a SKU in it at all.
	 *
	 * Every step is skipped when its input is missing — a modal for a product that is not
	 * quote-only, an unreadable chip combination, a Boost template not yet pasted into Boost
	 * admin — and the button then keeps the product-level SKU the template opened it on, which
	 * is the same SKU the single-variant card would have sent.
	 */
	const syncQuickViewQuoteSku = (modal) => {
		const button = modal.querySelector('[data-open-centralio-modal][data-sku]');
		if (!button) return;

		const variant = selectedQuickViewVariant(modal);
		if (!variant) return;

		const sku = [...modal.querySelectorAll('.cp-qv-variant-skus [data-cp-variant-id]')].find(
			(node) => node.dataset.cpVariantId === String(variant.id)
		)?.dataset.cpSku;
		if (!sku || button.dataset.sku === sku) return;

		button.dataset.sku = sku;
	};

	/**
	 * The same, for the pass the observer makes, and for the same reason `syncQuickViewPriceRows`
	 * makes one: the modal opens with every option row checked on its first value, so the SKU it
	 * was rendered with describes the product rather than the combination now lit up in it.
	 */
	const syncQuickViewQuoteSkus = () => {
		const modal = document.querySelector('#boost-sd__modal-quickview');
		if (modal) syncQuickViewQuoteSku(modal);
	};

	/**
	 * Lift the picked variant's shot to the top of the stacked image column.
	 *
	 * Boost does resolve the image behind a chip: on every pick its runtime moves `--selected`
	 * onto the matching `.boost-sd__thumbs-container-thumbs-item` and slides its carousel to the
	 * same media — synchronously, inside its own click handler, measured on the dev theme. But
	 * neither is visible in this modal. boost-plp.css hides the carousel (the design has no main
	 * image; the thumbnail strip *is* the gallery) and strips the ring off the selected thumb, so
	 * a shopper switching colour watched the column stay put, the graphite shot sitting unseen
	 * twenty tiles down a stack still scrolled to the anthracite one.
	 *
	 * The tile is brought to the top rather than scrolled to, which is what the PDP gallery does
	 * with the variant's featured media (blocks/_nova-product-media-gallery.liquid puts it first
	 * and keeps the rest in order): the column is then read from the top as usual, badge and
	 * QC-CA mark included — the overlay quickview/main.liquid draws on the first tile travels
	 * with the position, not the photo, so it is moved onto whichever tile now leads. The
	 * scroller is reset for the same reason: a column left mid-stack would show the tile the
	 * shopper had paused on, not the one their pick just named.
	 *
	 * The tile is found from the product blob — the variant the chips name, its `image`, and the
	 * position of that image in the `media` list the tiles were drawn from — rather than read
	 * off the `--selected` Boost puts on a thumb. That mark is only as current as Boost's
	 * carousel: a pick made while the previous one is still animating is dropped by it (slick's
	 * own guard), and the mark then stays on the old tile. Two chips clicked in the same frame
	 * is exactly what `applyQuickViewVariant` does on open, measured landing on the *first*
	 * click's image. The blob is what the price and SKU syncs already trust, and it has no such
	 * race. URLs are compared without their query, in case the two copies disagree on a `?v=`.
	 *
	 * Brought to the top with flex `order` (boost-plp.css, `--cp-lead`), never by moving the
	 * node: Boost addresses the strip by position — the tile it marks for a variant whose image
	 * is media N is `children[N]`, and the mark it clears is the one at the index it last set.
	 * A strip reordered in the DOM had it lighting the wrong tile from the second pick on and
	 * leaving the first pick's mark behind, measured on the dev theme before this was switched.
	 * Moving the overlay is safe because it lives inside a tile, not among them.
	 *
	 * A variant with no image of its own leads with nothing, the column back in media order —
	 * the same thing the PDP gallery does for it. An unreadable modal is left alone.
	 *
	 * Idempotent on the tile already leading, and it has to be: the observer repeats this on
	 * every mutation, and a scroll reset that ran each time would fight the shopper's own
	 * scrolling of the column.
	 */
	const LEAD_TILE = 'boost-sd__thumbs-container-thumbs-item--cp-lead';

	const mediaPath = (item) =>
		String(item?.image?.originalSrc ?? item?.src ?? item ?? '').split('?')[0];

	const syncQuickViewMedia = (modal) => {
		const strip = modal.querySelector('.boost-sd__thumbs-container-thumbs');
		if (!strip) return;

		const variant = selectedQuickViewVariant(modal);
		if (!variant) return;

		let media = [];
		try {
			media = JSON.parse(modal.dataset.product ?? '').media ?? [];
		} catch {
			return;
		}

		const wanted = variant.image ? mediaPath(variant.image) : '';
		const index = wanted ? media.findIndex((item) => mediaPath(item) === wanted) : -1;
		const lead = index < 0 ? null : strip.querySelector(`[data-index="${index}"]`);

		const current = strip.querySelector(`.${LEAD_TILE}`);
		if (current === lead) return;

		current?.classList.remove(LEAD_TILE);
		lead?.classList.add(LEAD_TILE);

		/* Back onto the first tile when nothing leads, where the template drew it. */
		const overlay = strip.querySelector('.cp-qv-image-overlay');
		const host = lead ?? strip.firstElementChild;
		if (overlay) host?.querySelector('.boost-sd__product-image-wrapper')?.prepend(overlay);

		const scroller = modal.querySelector('.boost-sd__slider');
		if (scroller) scroller.scrollTop = 0;
	};

	/**
	 * The same, for the pass the observer makes: `applyQuickViewVariant` drives Boost's chips on
	 * open, and the tile Boost selects in answer lands on a strip that was drawn in media order.
	 */
	const syncQuickViewMediaRows = () => {
		const modal = document.querySelector('#boost-sd__modal-quickview');
		if (modal) syncQuickViewMedia(modal);
	};

	/**
	 * Add to cart from the grid and from the quick view, without leaving the page.
	 *
	 * Boost adds the line itself and then sends the shopper to /cart, and no template can decline
	 * it: the grid's CTA carries an `addToCart` action
	 * (boostai/src/product-item/add-to-cart-button.liquid) and the modal's button carries no
	 * action at all, so there is no attribute either file could set to say "stay here". The add is
	 * taken over instead, and Boost's handler never sees the click.
	 *
	 * What replaces it is the theme's own add, step for step out of AddToCartComponent in
	 * assets/product-form.js: the same `Theme.routes.cart_add_url`, the same `fetchConfig`, the
	 * same `sections` list, and the same `CartAddEvent` at the end of it. Reusing that event is
	 * what makes the feedback the theme's own rather than a second implementation of it —
	 * `cart-icon.js` moves the header count off it, and a store switched to `cart_type: drawer`
	 * would get the drawer opening and refilling itself off the same dispatch, with nothing here
	 * to change.
	 *
	 * This store is on `cart_type: page` (config/settings_data.json), so today there is no drawer
	 * and no `cart-items-component` to name in `sections` — the list comes back empty and the
	 * parameter is left off. The count in the header is the whole of the visible answer, which is
	 * also what the shopper gets from a native card on this store.
	 */
	const cartSectionIds = () =>
		[...document.querySelectorAll('cart-items-component')]
			.map((node) => node.dataset.sectionId)
			.filter(Boolean);

	const themeAddToCart = (variantId, quantity) => {
		const body = new FormData();
		body.append('id', String(variantId));
		body.append('quantity', String(quantity));

		const sections = cartSectionIds();
		if (sections.length) body.append('sections', sections.join(','));

		const config = fetchConfig('javascript', {body});

		return fetch(Theme.routes.cart_add_url, {
			...config,
			headers: {...config.headers, Accept: 'text/html'},
		})
			.then((response) => response.json())
			.then((response) => {
				/*
				 * Shopify answers a refusal with a `status` and a message rather than a failed
				 * request — a line beyond the stock on hand, a quantity rule. Nothing is
				 * dispatched for one, so nothing announces a cart that did not change.
				 *
				 * There is nowhere to say so on screen: the grid card has no error slot the way
				 * the native card's form does, and the refusal is only reachable at all when the
				 * stock ran out between the grid being rendered and the button being pressed —
				 * every other case leaves the CTA disabled. Logged so it is diagnosable.
				 */
				if (response.status) {
					console.warn('[cp-plp] add to cart refused:', response.message);
					return false;
				}

				/*
				 * `source` names the *kind* of update, not the file that sent it, and cart-icon.js
				 * is the one thing that reads it: this value is the branch where `itemCount` is a
				 * quantity to add to the count on screen, and any other value is the branch where
				 * it is the new total. A quantity is what /cart/add.js can tell us — it answers
				 * with the line it added and not with the cart — so this is the branch that
				 * describes what actually happened, and it is the one the native add-to-cart on
				 * this store goes through too.
				 */
				document.body.dispatchEvent(
					new CartAddEvent({}, String(variantId), {
						source: 'product-form-component',
						itemCount: quantity,
						variantId: String(variantId),
						sections: response.sections,
					})
				);

				return true;
			})
			.catch((error) => {
				/* The cart is unchanged and the shopper still has the button, so there is nothing
				 * to undo — but Boost's own add was stopped to get here, so the failure is logged
				 * rather than swallowed. */
				console.error('[cp-plp] add to cart failed:', error);
				return false;
			});
	};

	/**
	 * The variant a grid CTA would add, or null when that is not what it does.
	 *
	 * The same `data-metadata` blob Boost reads, so the two cannot disagree about which cards add
	 * and which open the quick view: a card whose action is `quickView` — every multi-variant
	 * card, and every quote card with more than one variant — answers null here and is handed
	 * straight back to Boost untouched.
	 */
	const cardAddToCartVariant = (button) => {
		try {
			return JSON.parse(button.dataset.metadata ?? '')?.action?.addToCart?.productId ?? null;
		} catch {
			return null;
		}
	};

	/*
	 * Boost owns the quick view, so it is closed through its own close button rather than by
	 * stripping classes — the same deference onSortingCloseClick pays the sort panel, and it
	 * leaves Boost believing what is true.
	 */
	const closeQuickView = (modal) => {
		modal.querySelector('.boost-sd__modal-close-btn')?.click();
	};

	/**
	 * The press feedback, ported from AddToCartComponent in assets/product-form.js so a card in the
	 * Boost grid answers a press exactly as a native card does.
	 *
	 * Two halves, as it is there. `atc-added` swaps the CTA's two faces for
	 * ADD_TO_CART_TEXT_ANIMATION_DURATION — the styling of that is in assets/boost-plp.css, the
	 * markup in boostai/src/product-item/add-to-cart-button.liquid. Then a clone of the button's
	 * own cart glyph flies to the header count, which is the `data-fly-to-cart="icon"` flavour the
	 * native *card* uses: the product image is already sitting right above the button on a card, so
	 * a second copy of it flying reads as noise. The quick view leads its own button with the same
	 * glyph (boostai/src/quickview/main.liquid), so the flight is the same one from either.
	 *
	 * Both are fired on the press rather than on the response, again as the native card does — the
	 * add is optimistic there too, and a refusal is only reachable when the stock ran out between
	 * the grid being rendered and the button being pressed.
	 */
	const atcAnimationTimers = new WeakMap();

	const flashAddedState = (button) => {
		const previous = atcAnimationTimers.get(button);
		if (previous) clearTimeout(previous);

		button.classList.add('atc-added');
		atcAnimationTimers.set(
			button,
			setTimeout(() => {
				button.classList.remove('atc-added');
				atcAnimationTimers.delete(button);
			}, ADD_TO_CART_TEXT_ANIMATION_DURATION)
		);
	};

	/*
	 * Measured, not tracked: FlyToCart takes both rectangles as it is appended and animates a
	 * `position: fixed` element off them. So the quick view closing a moment later — which is what
	 * a successful add in there does — leaves the flight it started already fully described, with
	 * the header count it is aimed at still where it was when the button was pressed.
	 *
	 * `settings.add_to_cart_animation` gates only this half on the native card, and it reaches here
	 * through `Theme.settings` in snippets/scripts.liquid — a Boost template renders from Boost
	 * admin and cannot read a theme setting, so the flag the native card carries as an attribute on
	 * its own button has to be asked for globally. Read off the bare binding rather than off
	 * `window`, because scripts.liquid declares it with `const`: the name is in the global lexical
	 * scope, which a module sees, but it is not a property of `window`.
	 */
	const flyIconToCart = (button) => {
		if (Theme.settings?.add_to_cart_animation !== true) return;
		if (!customElements.get('fly-to-cart')) return;

		const cartIcon = document.querySelector('.header-actions__cart-icon');
		const glyph = button.querySelector('.cp-plp-cta-icon--cart');
		if (!cartIcon || !glyph) return;

		const flyer = document.createElement('fly-to-cart');
		flyer.classList.add('fly-to-cart--icon');
		flyer.appendChild(glyph.cloneNode(true));
		flyer.source = button;
		flyer.destination = cartIcon;

		document.body.appendChild(flyer);
	};

	/*
	 * A sold-out or in-store-only CTA (CP_TO_PDP) is not disabled: it opens the product page, so
	 * the shopper can see stores, stock and the other variants. On a card the target is the
	 * title link, which `applyVariant` keeps on the picked variant; in the quick view it is the
	 * "View full details" link. Registered ahead of `onAddToCartClick` and stops the event, so a
	 * sold-out card that still carries Boost's addToCart metadata never reaches either handler.
	 */
	const onToProductPageClick = (event) => {
		const button = event.target.closest?.(`.${CP_TO_PDP}`);
		if (!button) return;

		const scope = button.closest('.boost-sd__product-item, #boost-sd__modal-quickview');
		const link = scope?.querySelector('a.boost-sd__product-link[href], a.boost-sd__quick-view-details-link[href]');
		if (!link) return;

		event.preventDefault();
		event.stopImmediatePropagation();
		window.location.assign(link.href);
	};

	/*
	 * Capture phase on `document`, which is what makes this the *first* handler to see the click:
	 * Boost's own is delegated below it, so stopping the event here is what keeps it from adding
	 * the line a second time and navigating away afterwards.
	 *
	 * Everything this cannot answer for is left alone rather than guessed at — a disabled button,
	 * a card whose action is not `addToCart`, a modal whose chips name no variant — and Boost
	 * then does exactly what it did before.
	 */
	const onAddToCartClick = (event) => {
		const button = event.target.closest?.(
			'.boost-sd__button--select-option[data-metadata], #boost-sd__modal-quickview .boost-sd__btn-add-to-cart'
		);
		if (!button || button.disabled || button.classList.contains('boost-sd__button--disabled')) {
			return;
		}

		const modal = button.closest('#boost-sd__modal-quickview');
		let variantId = null;
		let quantity = 1;

		if (modal) {
			variantId = selectedQuickViewVariant(modal)?.id ?? null;

			const parsed = Number.parseInt(
				modal.querySelector('.boost-sd__input-number-input')?.value ?? '1',
				10
			);
			if (Number.isFinite(parsed) && parsed > 0) quantity = parsed;
		} else {
			variantId = cardAddToCartVariant(button);
		}

		if (!variantId) return;

		event.preventDefault();
		event.stopImmediatePropagation();

		/*
		 * The glyph flies from either button, because both leave the same question open: the only
		 * thing on the page that moves for an add on this store is the count in the header, and
		 * from inside the quick view the modal is covering even that.
		 *
		 * The two faces are the card's alone. A successful add closes the modal, so its button
		 * would be swapping to an "added" state on its way off the screen — and it carries no such
		 * face to swap to.
		 */
		if (!modal) flashAddedState(button);
		flyIconToCart(button);

		const added = themeAddToCart(variantId, quantity);

		/*
		 * A line added from inside the quick view closes it, because the modal is now covering the
		 * only confirmation there is: this store is on `cart_type: page`, so the answer to "did
		 * that work" is the count in the header, and nothing else on the page moves. Closing on a
		 * refusal instead would hide the fact that nothing happened, so the modal stays put for
		 * one and the shopper still has the button.
		 */
		if (modal) added.then((ok) => ok && closeQuickView(modal));
	};

	/**
	 * Open the Centralio form from a single-variant quote card without leaving the grid.
	 *
	 * Boost's own card handler sends any click that reaches it to the PDP, and the quote button
	 * (boostai/src/product-item/add-to-cart-button.liquid) is a plain button with no
	 * data-metadata for Boost to recognise as its own — so Boost treats it as a press on the card
	 * and navigates. The Centralio listener in assets/nova-centralio-modal.js is delegated on
	 * `document` in the bubble phase, which is after Boost's React root has already had the
	 * click, so its `stopPropagation` lands too late: the form opens and the page goes anyway.
	 *
	 * Capture phase on `document` is the only spot ahead of Boost, and stopping the event there
	 * also stops it reaching the Centralio listener — so the form is opened directly here,
	 * through the same `open(locale, sku)` that listener would have called. Scoped to cards:
	 * the quick-view modal is portalled to <body>, outside Boost's card handler, and
	 * `onQuickViewQuoteClick` below already has that path.
	 */
	const onCardQuoteClick = (event) => {
		const button = event.target.closest?.('[data-open-centralio-modal]');
		if (!button?.closest('.boost-sd__product-item')) return;

		const centralio = window.centralioModal;
		if (!centralio) return;

		event.preventDefault();
		event.stopPropagation();
		centralio.open(button.dataset.locale || 'fr', button.dataset.sku || null);
	};

	/**
	 * Close the quick view on the way to the Centralio form (scenario 4).
	 *
	 * A quote product with several variants opens the modal so the shopper can say which one they
	 * mean; pressing the quote CTA in there is the end of that errand, and leaving the modal
	 * standing behind the form would be two dialogs deep over a grid the shopper has finished
	 * with.
	 *
	 * Deliberately does not stop the event: the Centralio listener in assets/nova-centralio-modal.js
	 * is delegated on `document` and reads the SKU off this button, so it still has to reach it.
	 * Capture phase only orders the two, so the close lands before the form opens.
	 */
	const onQuickViewQuoteClick = (event) => {
		const button = event.target.closest?.('[data-open-centralio-modal]');
		const modal = button?.closest('#boost-sd__modal-quickview');
		if (!modal) return;

		closeQuickView(modal);

		/*
		 * Boost frees the page scroll as it closes, and if it does so on an animation rather than
		 * inline it lands after the Centralio modal has locked it — leaving the grid scrolling
		 * behind the form. Restated a frame later, and only while that form is actually open.
		 */
		requestAnimationFrame(() => {
			const centralio = document.getElementById('nova-centralio-modal');
			if (centralio && !centralio.hasAttribute('hidden')) {
				document.body.style.overflow = 'hidden';
			}
		});
	};

	/*
	 * A frame is waited out for the same reason `applyQuickViewVariant` waits one: Boost handles
	 * the same click and rewrites its half of the modal from it, and ours has to be the write
	 * that lands last. `change` covers the label's native activation of its radio, `click` the
	 * case where Boost has taken that click over and set the input itself; both end in the same
	 * idempotent write, so seeing a pick twice costs a selector match and changes nothing.
	 *
	 * The price row, the quote CTA and the image column are all restated, because all three
	 * describe the variant the chips name. Each call is a no-op when its half of the modal is
	 * not there, which is the usual case: a quote product hides its price and a buyable one
	 * carries no quote button.
	 */
	const onQuickViewSwatchPick = (event) => {
		const modal = event.target.closest?.('#boost-sd__modal-quickview');
		if (!modal || !event.target.closest('.boost-sd__product-swatch')) return;

		requestAnimationFrame(() => {
			syncQuickViewPrice(modal);
			syncQuickViewQuoteSku(modal);
			syncQuickViewAvailability(modal);
			syncQuickViewMedia(modal);
		});
	};

	/**
	 * Marked on `.boost-sd__quick-view` rather than on the modal shell, because that is the node
	 * Boost rebuilds per open — the same thing hydrateQuickViewWishlist relies on when it tests
	 * its placeholder for emptiness. Comparing the mark against the pending id rather than merely
	 * testing for it also re-applies when a reused shell is refilled from a different card.
	 *
	 * Applied once, then the stash is dropped: a shopper who changes a chip inside the modal has
	 * made a newer choice than the card's, and nothing here puts the card's back.
	 */
	const applyQuickViewVariant = () => {
		const pending = pendingQuickViewVariant;
		if (!pending) return;

		const modal = document.querySelector('#boost-sd__modal-quickview');
		const view = modal?.querySelector('.boost-sd__quick-view');
		if (!view || view.dataset.cpVariantApplied === pending.id) return;

		view.dataset.cpVariantApplied = pending.id;
		pendingQuickViewVariant = null;

		selectQuickViewOptions(modal, pending.id);

		/*
		 * Before hydrateQuickViewWishlist runs, so the heart is built pointing at this variant.
		 * The template can only hardcode `product.variants[0].id` there — it has no more idea
		 * which variant is meant than the price row does.
		 */
		const slot = modal.querySelector('.boost-sd__quick-view-wishlist');
		if (slot) slot.dataset.variantId = pending.id;

		/*
		 * A frame later, and re-queried: the chip clicks above hand Boost's runtime the variant,
		 * and whatever it writes to this row in response has to land before ours does, not after.
		 * The same reason reassertVariantImages waits one out on the card.
		 *
		 * The modal's own per-variant list is asked first, and the card's figures are the
		 * fallback for when it cannot answer. The two describe different things once the chips
		 * have settled — the card published the figures of the *representative* variant for the
		 * colour it was showing, which is that colour's first buyable size, while the rows here
		 * are now sitting on a size of their own — and the row has to agree with the chips
		 * beneath it rather than with the card that opened it.
		 */
		requestAnimationFrame(() => {
			if (syncQuickViewPrice(modal)) return;

			const row = modal.querySelector('.boost-sd__quick-view-price');
			if (row) setQuickViewPrice(row, pending.price, pending.compare, pending.discount);
		});
	};

	/**
	 * The wishlist heart in the quick-view modal (Figma 2089-10392).
	 *
	 * boostai/src/quickview/main.liquid cannot write Swish's own element: Boost's template parser
	 * drops the unknown `<wishlist-button-block>` tag and empties the wrapper it sat in, whether
	 * the markup is rendered from product-wishlist/main.liquid or pasted into the quick-view
	 * template directly. So that file emits `[data-cp-qv-wishlist]` as an empty placeholder and
	 * the button is built here instead, where Boost's parser never sees it.
	 *
	 * The heart glyph is cloned off a card rather than repeated as a string: the grid behind the
	 * modal has already rendered the same overlay (boostai/src/product-wishlist/main.liquid), so
	 * cloning keeps one copy of the path in the codebase. A modal opened before the grid has
	 * painted simply gets Swish's own glyph until the next pass, which is the same fallback the
	 * card has while its component script is still loading.
	 *
	 * Idempotent on the placeholder being empty, because the observer runs this on every mutation
	 * and Boost re-renders the modal on each open.
	 */
	const hydrateQuickViewWishlist = () => {
		for (const slot of document.querySelectorAll('.boost-sd__quick-view-wishlist')) {
			if (slot.firstElementChild) continue;

			/*
			 * The placeholder's own data attributes first, and the modal's `data-product` blob as
			 * the fallback — that one is part of Boost's stock quick-view markup, so it is the
			 * pair of values that cannot be dropped on the way through Boost's parser.
			 */
			let {productHandle, variantId} = slot.dataset;
			if (!productHandle) {
				const raw = slot.closest('#boost-sd__modal-quickview')?.dataset.product;
				try {
					const product = raw ? JSON.parse(raw) : null;
					productHandle = product?.handle;
					variantId = variantId || product?.variantId || product?.variants?.[0]?.id;
				} catch {
					// A blob Boost wrote in a shape we do not know is not worth guessing at.
				}
			}
			if (!productHandle) continue;

			const host = document.createElement('div');
			host.className = 'boost-product-wishlist wishlist-button-host';
			host.dataset.noRedirect = 'true';

			const button = document.createElement('wishlist-button-block');
			button.setAttribute('data-product-handle', productHandle);
			if (variantId) button.setAttribute('data-variant-id', variantId);
			button.setAttribute('show-icon', '');
			host.append(button);

			const glyph = document
				.querySelector('.boost-sd__product-item .wishlist-button-icon')
				?.cloneNode(true);
			if (glyph) host.append(glyph);

			slot.append(host);
		}
	};

	/*
	 * Drag-to-scroll for the quick-view image stack (Figma 2089-10344).
	 *
	 * The left column of the modal is every product shot stacked in one scroller, and the frame
	 * draws no scrollbar on it — assets/boost-plp.css hides the one the platform would otherwise
	 * paint. A wheel and a finger both still scroll it; a mouse without a wheel had nothing left,
	 * so the stack is also draggable, which is what the grab cursor in that file advertises.
	 *
	 * Mouse pointers only. Touch and pen already pan the scroller natively, and taking their
	 * pointer events would replace a fling that has momentum with one that does not.
	 *
	 * Delegated on `document` and registered once, so it survives every Boost re-render — the
	 * modal is rebuilt on each open.
	 */
	const QV_DRAG_THRESHOLD = 4;

	const qvDrag = {
		slider: null,
		pointerId: -1,
		startY: 0,
		startScrollTop: 0,
		moved: false,
		suppressClick: false,
	};

	const endQuickViewDrag = () => {
		qvDrag.slider?.classList.remove('boost-sd__slider--cp-dragging');
		qvDrag.slider = null;
		qvDrag.pointerId = -1;
		qvDrag.moved = false;
	};

	const onQuickViewDragStart = (event) => {
		if (event.pointerType !== 'mouse' || event.button !== 0) return;

		const slider = event.target.closest?.('#boost-sd__modal-quickview .boost-sd__slider');
		/* Nothing to drag while every shot already fits. */
		if (!slider || slider.scrollHeight <= slider.clientHeight) return;

		qvDrag.slider = slider;
		qvDrag.pointerId = event.pointerId;
		qvDrag.startY = event.clientY;
		qvDrag.startScrollTop = slider.scrollTop;
		qvDrag.moved = false;
		qvDrag.suppressClick = false;
	};

	const onQuickViewDragMove = (event) => {
		const slider = qvDrag.slider;
		if (!slider || event.pointerId !== qvDrag.pointerId) return;

		const delta = event.clientY - qvDrag.startY;

		/*
		 * A few pixels of slop before this counts as a drag, so that the shake in an ordinary
		 * click never suppresses it and never grabs the pointer.
		 */
		if (!qvDrag.moved) {
			if (Math.abs(delta) < QV_DRAG_THRESHOLD) return;
			qvDrag.moved = true;
			slider.classList.add('boost-sd__slider--cp-dragging');
			/* Keeps the drag alive once the pointer leaves the column, as a scrollbar would. */
			slider.setPointerCapture?.(event.pointerId);
		}

		slider.scrollTop = qvDrag.startScrollTop - delta;
	};

	const onQuickViewDragEnd = (event) => {
		if (!qvDrag.slider || event.pointerId !== qvDrag.pointerId) return;

		/*
		 * A drag ends on the tile it happens to be over, and the browser follows it with a click
		 * there. Boost binds its carousel's slide-select to exactly that click, so a drag would
		 * otherwise finish by acting on whichever image it stopped on.
		 */
		qvDrag.suppressClick = qvDrag.moved;
		endQuickViewDrag();
	};

	/*
	 * The swallow, in the capture phase so nothing further down the tree sees it either. A flag
	 * rather than a one-shot listener: if a release produces no click at all — the pointer left
	 * the window, the modal closed under it — the flag is cleared by the next press instead of
	 * sitting armed and eating a click the shopper meant.
	 */
	const onQuickViewDragClick = (event) => {
		if (!qvDrag.suppressClick) return;

		qvDrag.suppressClick = false;
		event.stopPropagation();
	};

	/*
	 * Every tile is an image element, and the browser's own image drag starts on the first pixel
	 * of movement — before the threshold above is reached — and then owns the gesture, so the
	 * shopper gets a ghost of the photo trailing the cursor instead of a scrolling column.
	 */
	const onQuickViewDragImage = (event) => {
		if (event.target.closest?.('#boost-sd__modal-quickview .boost-sd__slider')) {
			event.preventDefault();
		}
	};

	const decoratePlp = () => {
		// The grid, or the Recommendation widget on its own — its cards need the same passes.
		if (!document.querySelector('.boost-sd-container, .boost-sd__recommendation')) return;
		routeSearchPageFetches();
		applyQuickViewVariant();
		syncQuickViewPriceRows();
		syncQuickViewQuoteSkus();
		syncQuickViewAvailabilityRows();
		syncQuickViewSoldOutLabel();
		syncQuickViewMediaRows();
		hydrateQuickViewWishlist();
		relocateRefineBy();
		nameFilterToggle();
		nameDrawerChrome();
		moveSortingIntoDrawer();
		buildCardGalleryDots();
		labelPagination();
		unslickCrossSell();
		addCarouselPhoneBreakpoint();
		scopeCarouselSwatches();
		collapseSwatches();
		syncCardVariants();
		reassertVariantImages();
		injectPromotions();
		fixVariantPriceRows();
		localizeCardPrices();
		nameHouseBrand();
		translateFilterValues();
		sortFilterValues();
		translateOptionValues();
		syncViewMoreLabels();
		syncPriceRangeInputsSoon();
		formatPriceRangeChips();
	};

	const start = () => {
		register(searchAnchorSource);
		propsFor(['cp-search-anchor']);

		document.addEventListener('pointerover', onSearchIntent, {passive: true});
		document.addEventListener('focusin', onSearchIntent, {passive: true});
		document.addEventListener('click', onSortingCloseClick);
		document.addEventListener('click', rememberQuickViewVariant, true);
		document.addEventListener('click', onToProductPageClick, true);
		document.addEventListener('click', onAddToCartClick, true);
		document.addEventListener('click', onCardQuoteClick, true);
		document.addEventListener('click', onQuickViewQuoteClick, true);
		document.addEventListener('pointerdown', onQuickViewDragStart);
		document.addEventListener('pointermove', onQuickViewDragMove);
		document.addEventListener('pointerup', onQuickViewDragEnd);
		document.addEventListener('pointercancel', onQuickViewDragEnd);
		document.addEventListener('click', onQuickViewDragClick, true);
		document.addEventListener('pointerdown', onCardGalleryDragStart);
		document.addEventListener('pointermove', onCardGalleryDragMove);
		document.addEventListener('pointerup', onCardGalleryDragEnd);
		document.addEventListener('pointercancel', onCardGalleryDragEnd);
		document.addEventListener('click', onCardGalleryDragClick, true);
		document.addEventListener('click', onCardInfoClick, true);
		document.addEventListener('click', onPaginationClick, true);
		document.addEventListener('animationend', onCrossSellRowShown);
		document.addEventListener('dragstart', onQuickViewDragImage);
		document.addEventListener('change', onSwatchChange);
		document.addEventListener('change', onQuickViewSwatchPick);
		document.addEventListener('click', onQuickViewSwatchPick);
		document.addEventListener('mouseout', onSwatchPointerOut, {passive: true});
		document.addEventListener('keydown', onSwatchKeydown);
		document.addEventListener('keydown', onPriceRangeKeydown, true);
		document.addEventListener('blur', onPriceRangeBlur, true);
		document.addEventListener('input', onPriceRangeInput, true);

		hookBoostScrollToTop();
		splitMobilePanel();
		decoratePlp();
		localizePanel();

		/*
		 * The mobile PLP passes are gated on `MOBILE_PLP`, and the observer below only fires on
		 * DOM changes — so crossing the breakpoint on a resize alone (a phone turned on its side,
		 * a desktop device toolbar) would leave the page in the state it was built for. This is
		 * the one signal that says so.
		 */
		MOBILE_PLP.addEventListener('change', () => {
			decoratePlp();
			localizePanel();
		});

		new MutationObserver(() => {
			refreshAnchor();
			rebindSearchInputs();
			splitMobilePanel();
			// Before `localizePanel`, so a `data-cp-i18n` a pass adds is swapped in the same pass.
			decoratePlp();
			localizePanel();
		}).observe(document.body, {childList: true, subtree: true});
	};

	if (document.body) {
		start();
	} else {
		document.addEventListener('DOMContentLoaded', start, {once: true});
	}
})();
